// Operator-requested destinations with destination-specific trip planning.
// Road hire is quoted separately from attraction entry and other arrangements.
export const TOURISM_DESTINATIONS = [
  {
    "slug": "bhimbetka",
    "name": "Bhimbetka",
    "state": "Madhya Pradesh",
    "heroTagline": "Bhimbetka Taxi, Car Hire & Tempo Traveller Booking",
    "attractions": [
      "Bhimbetka Rock Shelters",
      "Prehistoric cave paintings"
    ],
    "description": "Book Bhimbetka taxi service and car hire for rock-shelter visits. Arrange cab pickup, sightseeing waiting and Tempo Traveller rental for your group in advance.",
    "pickup": "Your hotel or address, or an agreed Bhimbetka parking point",
    "taxiIntro": [
      "Bhimbetka is known for prehistoric cave paintings and rock shelters, recognised as a UNESCO World Heritage site. Plan the rock-shelter visit with a clear pickup point and time for sightseeing.",
      "Cab booking or car hire can combine the requested pickup, waiting during the visit and the final drop. Share the full stop sequence and tell us whether the vehicle should remain with you or return for an agreed pickup.",
      "For a family or group trip, compare cars, SUVs and Tempo Travellers using passenger and luggage counts. Vehicle availability, dispatch and the complete road-travel quote are confirmed in advance; monument entry and guided visits are separate."
    ],
    "tempoUseCases": [
      {
        "title": "Rock-shelter group visit",
        "desc": "Share the number of travellers, planned time at the rock shelters and an agreed parking meeting point. Confirm the return pickup before the group begins sightseeing."
      },
      {
        "title": "Hotel pickup and onward transfer",
        "desc": "List the starting hotel and final destination, including any additional stops. Luggage and waiting requirements help the team confirm a suitable Traveller and trip quote."
      }
    ],
    "nearbyCities": [
      "bhopal",
      "sanchi",
      "rani-kamlapati-railway-station"
    ],
    "note": "For Bhimbetka's prehistoric rock shelters and cave paintings, share the pickup address, planned visit duration and return destination. Agree the vehicle parking and meeting point; monument entry and guided visits are separate from road hire.",
    "intent": "general"
  },
  {
    "slug": "chanderi",
    "name": "Chanderi",
    "state": "Madhya Pradesh",
    "heroTagline": "Chanderi Taxi Service, Cab Booking & Group Hire",
    "attractions": [
      "Chanderi Fort",
      "Handwoven Chanderi sarees"
    ],
    "description": "Arrange Chanderi taxi and cab booking for fort sightseeing and saree shopping. Compare car hire and Tempo Traveller rental with driver for your complete itinerary.",
    "pickup": "Your Chanderi hotel, town address or an agreed sightseeing meeting point",
    "taxiIntro": [
      "Chanderi combines a historic fort with a handwoven-saree tradition. A useful sightseeing plan allows time for both heritage stops and shopping rather than treating every stop as a quick drop.",
      "For car hire, send the starting address, selected shops or meeting points, fort-visit plan and return time. The team can quote the requested journey and waiting after reviewing the complete itinerary.",
      "If several families are travelling together, include each pickup and the luggage expected after shopping. Cars, SUVs and Tempo Travellers can be requested in advance, with the exact vehicle and dispatch arrangements confirmed before travel."
    ],
    "tempoUseCases": [
      {
        "title": "Fort and heritage group outing",
        "desc": "Choose a common pickup and list the sightseeing stops in order. Agree the vehicle parking and regrouping point before the group leaves the Traveller."
      },
      {
        "title": "Saree-shopping group travel",
        "desc": "Provide the shopping-stop sequence and expected waiting time. Include bags and purchases when discussing luggage space and the return journey."
      }
    ],
    "nearbyCities": [
      "orchha",
      "gwalior",
      "bhopal"
    ],
    "note": "For a Chanderi fort visit and saree-shopping trip, list the sightseeing and shopping stops in order. Share your hotel or town pickup pin and allow for waiting at each stop when requesting the cab or Traveller quote.",
    "intent": "general"
  },
  {
    "slug": "rani-kamlapati-railway-station",
    "name": "Rani Kamlapati Railway Station",
    "state": "Madhya Pradesh",
    "heroTagline": "Rani Kamlapati Railway Station Taxi & Tempo Traveller Pickup",
    "attractions": [],
    "description": "Book Rani Kamlapati Railway Station taxi pickup in Bhopal. Arrange cab or car hire and group Tempo Traveller transfers with train details and a confirmed meeting point.",
    "pickup": "Rani Kamlapati Railway Station, Bhopal — agreed exit or parking meeting point",
    "taxiIntro": [
      "This booking page is for Rani Kamlapati Railway Station in Bhopal. Copy the exact station name from your ticket so the pickup is planned for the correct railway arrival.",
      "Share the train number, arrival or departure time, passenger count, luggage and final hotel or destination. The driver meeting point should be agreed at a suitable station exit or parking area before dispatch.",
      "A car, SUV or Tempo Traveller can be requested for a station pickup, station drop or onward trip. Tell us about additional hotel stops or a return train connection; availability, waiting arrangements and the full fare are confirmed before the booking is final."
    ],
    "tempoUseCases": [
      {
        "title": "Group train-arrival pickup",
        "desc": "Send the train details and one group contact, plus the total passengers and luggage. Agree a station parking meeting point suitable for the selected Traveller."
      },
      {
        "title": "Hotel collection for a train departure",
        "desc": "List the hotel pickups and train departure time. The team will confirm the collection sequence and station drop plan for the requested date."
      }
    ],
    "nearbyCities": [
      "bhopal",
      "sanchi",
      "bhimbetka",
      "indore"
    ],
    "note": "For Rani Kamlapati Railway Station pickup in Bhopal, send the station name printed on your ticket, train number, arrival time and luggage count. Agree the station exit or parking meeting point and final drop address before dispatch.",
    "intent": "railway"
  },
  {
    "slug": "mandu",
    "name": "Mandu",
    "state": "Madhya Pradesh",
    "heroTagline": "Mandu Sightseeing Taxi, Car Hire & Tempo Traveller Rental",
    "attractions": [
      "Jahaz Mahal",
      "Rani Roopmati's Pavilion",
      "Historic Afghan architecture"
    ],
    "description": "Book Mandu taxi service and car hire for Jahaz Mahal and Rani Roopmati’s Pavilion. Plan sightseeing cab booking or Tempo Traveller rental with agreed stops and waiting.",
    "pickup": "Your Mandu hotel, agreed parking point or starting address",
    "taxiIntro": [
      "Mandu is an ancient fortress city known for Afghan architecture, Jahaz Mahal and Rani Roopmati’s Pavilion. Include the specific monuments you wish to see when planning the day’s road travel.",
      "For a sightseeing taxi or car on hire, provide the pickup address, order of visits and time needed at each stop. Add the final hotel or return destination so the quote covers the full itinerary.",
      "Tempo Traveller hire can keep a larger group on one agreed schedule. Share passenger numbers, luggage and any overnight stops; monument entry, guides and other attraction arrangements are separate from the confirmed vehicle booking."
    ],
    "tempoUseCases": [
      {
        "title": "Mandu monument circuit",
        "desc": "List Jahaz Mahal, Rani Roopmati’s Pavilion and your other chosen visits. Agree the stop order, waiting and regrouping points for the whole group."
      },
      {
        "title": "Overnight heritage trip",
        "desc": "Send the accommodation addresses, arrival day and final return plan. Include luggage and all requested road transfers when comparing Traveller options."
      }
    ],
    "nearbyCities": [
      "indore",
      "maheshwar",
      "ujjain"
    ],
    "note": "For Mandu sightseeing, list Jahaz Mahal, Rani Roopmati's Pavilion and any other requested stops in your itinerary. Share the overnight stay or final return point so monument waiting and road travel can be quoted together.",
    "intent": "general"
  },
  {
    "slug": "kanha",
    "name": "Kanha National Park",
    "state": "Madhya Pradesh",
    "heroTagline": "Kanha Road Transfers, Taxi Service & Tempo Traveller Hire",
    "attractions": [
      "Kanha National Park",
      "Tiger reserve and wildlife"
    ],
    "description": "Arrange Kanha taxi and car hire for resort and park-gate road transfers. Book a cab or Tempo Traveller for your group; safari permits and safari vehicles are separate.",
    "pickup": "Your confirmed Kanha resort, park reporting point or starting address",
    "taxiIntro": [
      "Kanha National Park is a tiger-reserve destination for wildlife travel. Road-transfer bookings need the exact resort or park reporting point rather than only the name Kanha.",
      "Share the gate and reporting time stated in your safari arrangements, along with the pickup address and luggage count. The taxi or car-hire itinerary should cover the agreed road transfer and any hotel changes or onward drop.",
      "A Tempo Traveller can be requested for a group travelling to accommodation or a reporting point. Park entry, safari permits, guides and safari vehicles are separate arrangements and are not included in the taxi or Traveller road-hire quote."
    ],
    "tempoUseCases": [
      {
        "title": "Resort group transfer",
        "desc": "Provide the exact accommodation address, arrival timing, passengers and luggage. Confirm access and the group drop point for the requested Traveller."
      },
      {
        "title": "Park reporting-point road transfer",
        "desc": "Use the gate and reporting time from the group’s safari booking. Road transport is quoted separately from permits, guides and the vehicle used inside the park."
      }
    ],
    "nearbyCities": [
      "jabalpur",
      "bandhavgarh",
      "umaria"
    ],
    "note": "For Kanha National Park road transfers, provide the exact resort, park gate and reporting time from your booking. Safari permits, guides and safari vehicles are arranged separately; the taxi or Tempo Traveller quote covers the agreed road journey.",
    "intent": "general"
  },
  {
    "slug": "maheshwar",
    "name": "Maheshwar",
    "state": "Madhya Pradesh",
    "heroTagline": "Maheshwar Taxi Service, Car Rental & Tempo Traveller Hire",
    "attractions": [
      "Ahilya Fort",
      "Narmada riverfront",
      "Maheshwari sarees"
    ],
    "description": "Book Maheshwar taxi service for Ahilya Fort, Narmada riverfront and saree-shopping stops. Compare cab booking, car hire and group Tempo Traveller rental with driver.",
    "pickup": "Your Maheshwar hotel, town address or agreed accessible meeting point",
    "taxiIntro": [
      "Maheshwar is a Narmada-bank temple town known for Ahilya Fort and Maheshwari sarees. A combined heritage and shopping itinerary should identify each desired stop and the time you want to spend there.",
      "For taxi booking or car rental with driver, share the pickup address, riverfront or fort visit and final return point. Agree a vehicle-accessible drop and meeting place for sightseeing on foot.",
      "Groups can compare a Tempo Traveller with smaller vehicles using the passenger and luggage totals. Add shopping bags, hotel transfers and any onward journey before the team confirms availability and the trip quote."
    ],
    "tempoUseCases": [
      {
        "title": "Fort and riverfront group visit",
        "desc": "Agree the pickup and an accessible drop point for Ahilya Fort and the Narmada riverfront. Plan when and where the group will return to the vehicle."
      },
      {
        "title": "Maheshwari saree-shopping itinerary",
        "desc": "List the requested shopping stops and waiting time. Include likely luggage and purchases when choosing a group vehicle and planning the final drop."
      }
    ],
    "nearbyCities": [
      "mandu",
      "indore",
      "ujjain"
    ],
    "note": "For Maheshwar's Ahilya Fort, Narmada riverfront and saree-shopping visits, agree an accessible drop point and the stop sequence. Send the pickup address, expected waiting time and onward destination before requesting car or group hire.",
    "intent": "general"
  },
  {
    "slug": "ujjain",
    "name": "Ujjain",
    "state": "Madhya Pradesh",
    "heroTagline": "Ujjain Taxi, Cab Booking & Pilgrimage Group Hire",
    "attractions": [
      "Mahakaleshwar Jyotirlinga Temple"
    ],
    "description": "Arrange Ujjain taxi service and cab booking for Mahakaleshwar visits. Compare car hire and Tempo Traveller rental with driver, confirmed pickup and a return plan.",
    "pickup": "Your Ujjain hotel, address or agreed temple-area parking point",
    "taxiIntro": [
      "Ujjain is a holy city known for the Mahakaleshwar Jyotirlinga temple. Give your planned temple-visit time when requesting a taxi so the road journey can be organised around your itinerary.",
      "Car hire can include a hotel pickup, an agreed vehicle-accessible drop and the return collection. Share any additional stops, luggage or onward destination before asking for the fare.",
      "For a pilgrimage group, request a suitable Tempo Traveller using the full passenger count. Confirm parking, waiting and the meeting point in advance; vehicle hire does not include temple entry or darshan arrangements."
    ],
    "tempoUseCases": [
      {
        "title": "Mahakaleshwar pilgrimage group",
        "desc": "Share the planned visit time and hotel pickup with the group count. Agree the parking and post-visit meeting point before the Traveller is dispatched."
      },
      {
        "title": "Multi-pickup family pilgrimage",
        "desc": "List the family pickup addresses, luggage and final return points. Confirm a practical collection sequence and waiting plan for the full group."
      }
    ],
    "nearbyCities": [
      "indore",
      "bhopal",
      "mandu",
      "maheshwar"
    ],
    "note": "For Mahakaleshwar temple travel in Ujjain, share your planned darshan time, hotel address and passenger count. Confirm a road-accessible parking and return meeting point; temple entry or darshan arrangements are separate from vehicle hire.",
    "intent": "pilgrimage",
    "tripPlanning": [
      {
        "title": "Ujjain hotel → Mahakaleshwar Temple → agreed return",
        "description": "Give the planned darshan time and agree the vehicle-accessible parking and return meeting point. Include waiting, bags and walking or boarding needs; temple entry and darshan arrangements are separate from the vehicle booking."
      },
      {
        "title": "Indore → Ujjain Mahakaleshwar visit → Indore or Ujjain stay",
        "description": "Send the starting address, requested temple visit and final hotel or return destination. Specify whether the vehicle waits or remains overnight and include every passenger and bag before the full road duty is quoted."
      }
    ]
  },
  {
    "slug": "orchha",
    "name": "Orchha",
    "state": "Madhya Pradesh",
    "heroTagline": "Orchha Taxi Service, Car Hire & Tempo Traveller Rental",
    "attractions": [
      "Raja Mahal",
      "Jahangir Mahal",
      "Shri Ram Raja Temple",
      "Betwa-side chhatris"
    ],
    "description": "Book Orchha taxi and car hire for palaces, temples and Betwa-side chhatris. Arrange cab booking or Tempo Traveller rental with a complete sightseeing and drop plan.",
    "pickup": "Your Orchha hotel, address or agreed sightseeing meeting point",
    "taxiIntro": [
      "Orchha is a historic town on the Betwa River, known for palaces, temples and chhatris. Include your chosen heritage visits in the booking request so sightseeing time and road travel can be planned together.",
      "A taxi or car with driver can be requested for hotel pickup, the agreed sightseeing stops and the final drop. For a one-way journey, send the next destination and luggage count rather than only a list of monuments.",
      "Tempo Traveller rental can be arranged for a group following the same itinerary, subject to advance availability confirmation. Agree the meeting points and waiting schedule; attraction entry and guide services are separate from vehicle hire."
    ],
    "tempoUseCases": [
      {
        "title": "Palaces, temples and chhatris circuit",
        "desc": "List the group’s selected heritage stops and visit duration. Agree where the Traveller waits and where the group meets after each visit."
      },
      {
        "title": "Hotel checkout and onward group travel",
        "desc": "Combine checkout pickup with any remaining sightseeing and the final drop. Include every passenger and bag when confirming the vehicle and quote."
      }
    ],
    "nearbyCities": [
      "khajuraho",
      "chhatarpur",
      "gwalior"
    ],
    "note": "For an Orchha palace, temple and chhatri itinerary, share the hotel pickup, sightseeing order and time planned near the Betwa River. Include luggage and the next destination when requesting a one-way or return car or Traveller booking.",
    "intent": "general",
    "tripPlanning": [
      {
        "title": "Orchha hotel → Raja Mahal / Jahangir Mahal → Ram Raja Temple → chhatris",
        "description": "Choose the palace and temple visits and allow time on foot near the Betwa-side chhatris. Agree parking, return meeting points and waiting for each stop; attraction entry and guided visits are separate from road hire."
      },
      {
        "title": "Orchha hotel checkout → selected heritage stops → Khajuraho",
        "description": "List the visits before departure and the complete Khajuraho hotel address. Include all luggage, any overnight plan and the final one-way or return requirement so sightseeing waiting and onward road travel are quoted together."
      }
    ]
  },
  {
    "slug": "satpura",
    "name": "Satpura National Park",
    "state": "Madhya Pradesh",
    "heroTagline": "Satpura Taxi Transfers, Car Hire & Tempo Traveller Booking",
    "attractions": [
      "Satpura National Park",
      "Satpura scenery and wildlife"
    ],
    "description": "Book Satpura National Park taxi transfers, car hire or Tempo Traveller rental for resort and reporting-point travel. Confirm exact locations; safari arrangements are separate.",
    "pickup": "Your Satpura accommodation or exact agreed road-transfer meeting point",
    "taxiIntro": [
      "This page covers road travel for Satpura National Park visits. Because Satpura also names a wider range, share the precise accommodation and destination map pins when enquiring.",
      "Taxi and car-hire requests should include the park reporting point from your own arrangements, the required time and the return or onward destination. Specify any hotel changes and luggage so the complete road itinerary can be quoted.",
      "A group can request Tempo Traveller hire for its agreed road transfers after access and availability are confirmed. Safari permits, guides and safari vehicles are separate from the taxi or Traveller booking."
    ],
    "tempoUseCases": [
      {
        "title": "Group accommodation transfer",
        "desc": "Send the exact resort or hotel location rather than only Satpura. Include luggage, arrival details and the final road-accessible drop point."
      },
      {
        "title": "Reporting-point and return road travel",
        "desc": "Provide the location and time from your park arrangements, plus the return plan. The Traveller quote covers road transfers; safari services and permits are separate."
      }
    ],
    "nearbyCities": [
      "bhopal",
      "bhimbetka",
      "rani-kamlapati-railway-station"
    ],
    "note": "For Satpura National Park transfers, give the exact accommodation and park reporting point; 'Satpura' can also mean the wider range. Confirm the road pickup and drop separately from safari permits, guides and safari vehicles.",
    "intent": "general"
  },
  {
    "slug": "sanchi",
    "name": "Sanchi",
    "state": "Madhya Pradesh",
    "heroTagline": "Sanchi Taxi Service, Car Rental & Tempo Traveller Hire",
    "attractions": [
      "Great Stupa of Sanchi",
      "Sanchi Buddhist monuments"
    ],
    "description": "Arrange Sanchi taxi service and cab booking for the Great Stupa and Buddhist monuments. Compare car hire and Tempo Traveller rental with sightseeing waiting and return travel.",
    "pickup": "Your starting hotel or address, or an agreed Sanchi parking point",
    "taxiIntro": [
      "Sanchi is known for its Great Stupa and Buddhist monuments, a UNESCO World Heritage site. Plan the road journey alongside the time needed for the heritage visit.",
      "For a taxi or car-hire request, give the starting address, selected monument stops and final drop. Agree the parking and meeting point so waiting during sightseeing is included in the itinerary sent for a quote.",
      "Groups can request a Tempo Traveller with driver after sharing passenger numbers and luggage. Vehicle availability and the fare are confirmed in advance; monument tickets and guides are separate from transport."
    ],
    "tempoUseCases": [
      {
        "title": "Buddhist-monuments group visit",
        "desc": "Choose a common pickup and state the expected visit duration. Confirm the parking and group meeting point before beginning the heritage walk."
      },
      {
        "title": "Sanchi visit with an onward drop",
        "desc": "Include the final hotel or destination after sightseeing, plus luggage. Ask for a quote covering the full road journey and requested waiting."
      }
    ],
    "nearbyCities": [
      "bhopal",
      "rani-kamlapati-railway-station",
      "bhimbetka"
    ],
    "note": "For a Sanchi Great Stupa and Buddhist-monuments visit, provide the starting address, sightseeing duration and return destination. Agree the parking and group meeting point; monument entry and guides are separate from the vehicle quote.",
    "intent": "general"
  },
  {
    "slug": "bhedaghat",
    "name": "Bhedaghat",
    "state": "Madhya Pradesh",
    "heroTagline": "Bhedaghat Sightseeing Taxi, Cab Booking & Group Rental",
    "attractions": [
      "Marble Rocks",
      "Dhuandhar Waterfalls",
      "Narmada River"
    ],
    "description": "Book Bhedaghat taxi service for Marble Rocks and Dhuandhar Waterfalls near Jabalpur. Compare cab booking, car hire and Tempo Traveller rental for your sightseeing group.",
    "pickup": "Your Jabalpur or Bhedaghat address, hotel or agreed parking point",
    "taxiIntro": [
      "Bhedaghat near Jabalpur is known for the Marble Rocks and Dhuandhar Waterfalls along the Narmada River. Put both stops in the itinerary if you want to visit them on the same journey.",
      "For taxi booking or car hire, send the starting address, time planned at each attraction and final drop point. Agree the vehicle parking and return meeting location for each sightseeing stop.",
      "Tempo Traveller rental can be requested for a shared family or group itinerary, with vehicle availability confirmed in advance. Any boat outing, attraction ticket or guide is arranged separately from the road-transport booking."
    ],
    "tempoUseCases": [
      {
        "title": "Marble Rocks and waterfall outing",
        "desc": "List both Marble Rocks and Dhuandhar Waterfalls with the time the group needs at each. Agree accessible drop and meeting points for the Traveller."
      },
      {
        "title": "Jabalpur pickup and return sightseeing",
        "desc": "Share the exact Jabalpur pickup, selected Bhedaghat stops and final return address. Include luggage and waiting before the team confirms the vehicle quote."
      }
    ],
    "nearbyCities": [
      "jabalpur",
      "kanha",
      "katni"
    ],
    "note": "For Bhedaghat sightseeing near Jabalpur, list Marble Rocks and Dhuandhar Waterfalls separately with the time needed at each stop. Share the final drop point; any boat outing or attraction ticket is separate from the taxi or Traveller booking.",
    "intent": "general"
  },
  {
    "slug": "ayodhya",
    "name": "Ayodhya",
    "state": "Uttar Pradesh",
    "heroTagline": "Ayodhya Taxi, Car Hire & Tempo Traveller Booking",
    "attractions": [
      "Shri Ram Janmabhoomi Mandir",
      "Hanuman Garhi",
      "Ram Ki Paidi",
      "Kanak Bhawan"
    ],
    "description": "Book Ayodhya taxi service, cab booking and car hire for Ram Mandir visits, hotel transfers and outstation trips. Compare Tempo Traveller rental for your group.",
    "pickup": "Your Ayodhya hotel, the exact station on your ticket, airport arrival point or an agreed accessible meeting point",
    "taxiIntro": [
      "An Ayodhya visit can combine Shri Ram Janmabhoomi Mandir, Hanuman Garhi, Kanak Bhawan and the Saryu riverfront at Ram Ki Paidi. Tell us which places you want to visit and how long you expect to spend at each, so the road journey and waiting can be planned together.",
      "For railway or airport pickup, send the arrival details exactly as printed on your ticket and the address of your hotel or dharamshala. Agree the vehicle-accessible drop and return meeting points for temple visits; share any walking or boarding assistance needs before selecting the vehicle.",
      "Request local car hire, a one-way drop or a return itinerary linking Ayodhya with Lucknow, Prayagraj or Varanasi. Include overnight stays, passengers and bags. The team confirms the selected vehicle, pickup arrangement and complete fare in advance; temple arrangements and admission requirements are separate from road transport."
    ],
    "tempoUseCases": [
      {
        "title": "Family Ram Mandir and Ayodhya darshan trip",
        "desc": "List the hotel collections, Ram Mandir visit, Hanuman Garhi and riverfront stops in your preferred order. Share the group's mobility and luggage needs, and agree accessible parking and regrouping points."
      },
      {
        "title": "Ayodhya with Prayagraj or Varanasi",
        "desc": "Send the full pilgrimage itinerary, accommodation addresses and final drop city. Request a Traveller quote covering all road transfers, planned waiting and overnight duties, with availability confirmed for your dates."
      }
    ],
    "nearbyCities": [
      "lucknow",
      "prayagraj",
      "varanasi"
    ],
    "note": "For Ayodhya darshan, list your temple stops, hotel and exact arrival station or airport details. Agree accessible drop and return meeting points, waiting and overnight travel before confirming the vehicle and full fare.",
    "intent": "pilgrimage",
    "tripPlanning": [
      {
        "title": "Ayodhya hotel → Ram Mandir → Hanuman Garhi → Ram Ki Paidi",
        "description": "Give the preferred temple order and time for darshan or a riverfront visit. Agree accessible road drops and return meeting points, and include any Kanak Bhawan stop before the vehicle duty is quoted."
      },
      {
        "title": "Ayodhya → Prayagraj → Varanasi itinerary",
        "description": "List the hotel nights, pilgrimage stops and final drop in order. Specify whether the vehicle stays with the group between visits, and include all bags and overnight duties in the full route request."
      }
    ]
  },
  {
    "slug": "mathura",
    "name": "Mathura",
    "state": "Uttar Pradesh",
    "heroTagline": "Mathura Taxi Service, Cab Booking & Tempo Traveller Hire",
    "attractions": [
      "Shri Krishna Janmabhoomi",
      "Vishram Ghat",
      "Banke Bihari Temple, Vrindavan",
      "Prem Mandir, Vrindavan"
    ],
    "description": "Arrange Mathura taxi and car hire for Krishna Janmabhoomi, Vishram Ghat and Vrindavan visits. Book a cab or Tempo Traveller with agreed pickup and waiting.",
    "pickup": "Mathura Junction, your Mathura hotel or dharamshala, or an agreed accessible meeting point",
    "taxiIntro": [
      "Mathura sightseeing can include Shri Krishna Janmabhoomi and Vishram Ghat on the Yamuna. Vrindavan is a separate stop on a wider itinerary, with destinations such as Banke Bihari Temple and Prem Mandir. Name the individual temples you want to visit when requesting your cab quote.",
      "For a Mathura Junction pickup, send the train number, arrival time and hotel or dharamshala address. A temple itinerary should include waiting and an agreed meeting point for each return pickup. Tell us whether your party needs a convenient boarding point and how much luggage will remain in the vehicle.",
      "Car hire can be requested for Mathura visits, a Mathura–Vrindavan day plan or onward travel to Agra, New Delhi or Jaipur. Share the stop sequence, final drop and whether the vehicle stays with you. Availability, access arrangements and the complete fare are confirmed before booking."
    ],
    "tempoUseCases": [
      {
        "title": "Mathura–Vrindavan temple group",
        "desc": "Combine your chosen Mathura and Vrindavan temples in one written itinerary. Confirm the group meeting points and waiting at each stop, with a vehicle suited to the passengers, bags and pickup access."
      },
      {
        "title": "Railway arrival and onward Agra travel",
        "desc": "Share the Mathura Junction arrival, hotel collection and planned Agra drop or sightseeing stops. Include luggage and any return train connection so the team can quote the complete group journey."
      }
    ],
    "nearbyCities": [
      "agra",
      "new-delhi",
      "jaipur"
    ],
    "note": "Distinguish Mathura stops from Vrindavan stops when booking. Send the exact temple names, Mathura Junction train details if applicable, hotel address and agreed parking or meeting points; confirm waiting and the final drop in your quote.",
    "intent": "pilgrimage",
    "tripPlanning": [
      {
        "title": "Mathura Junction → Krishna Janmabhoomi → Vishram Ghat → hotel",
        "description": "Send the exact train arrival and hotel address, then allow waiting for the temple and riverside visits. Confirm a separate pickup meeting point for each stop and the final return or hotel drop."
      },
      {
        "title": "Mathura → Banke Bihari Temple → Prem Mandir → Vrindavan or Mathura drop",
        "description": "Name the temples and preferred visit order when combining the two towns. Agree parking, time on foot and return collections, and state whether the vehicle finishes in Vrindavan or returns to Mathura."
      }
    ]
  },
  {
    "slug": "jaipur",
    "name": "Jaipur",
    "state": "Rajasthan",
    "heroTagline": "Jaipur Taxi, Cab Booking & Tempo Traveller Rental",
    "attractions": [
      "Hawa Mahal",
      "Amber Palace (Amer Fort)",
      "City Palace",
      "Jantar Mantar"
    ],
    "description": "Book Jaipur taxi service and car hire for Hawa Mahal, Amber Palace and city sightseeing. Compare cab booking and Tempo Traveller rental for your itinerary.",
    "pickup": "Your Jaipur hotel or address, Jaipur railway arrival point, or the airport meeting point confirmed for your flight",
    "taxiIntro": [
      "Jaipur's landmark stops include Hawa Mahal, City Palace and Jantar Mantar, while Amber Palace is a separate excursion towards Amer. Choose between a brief Hawa Mahal photo stop and a fuller palace visit, then list the remaining monuments in the order you want to see them.",
      "For arrival transfers, share your flight or train details, bags and the complete hotel address. If your plan includes old-city shopping or several hotel collections, provide the shop or meeting-point pins and expected waiting. Confirm suitable boarding and parking points for the selected car or Traveller.",
      "A Jaipur booking can cover local sightseeing, wedding or business transport, or a longer Rajasthan journey through Udaipur, Jodhpur or Jaisalmer. Give the overnight stops and final return or drop city. The quote is confirmed for the complete road itinerary; monument tickets, guides and other activities are separate."
    ],
    "tempoUseCases": [
      {
        "title": "Amber Palace and Jaipur heritage outing",
        "desc": "Plan the Amer excursion and old-city monuments with a common group pickup, stop order and return time. Include walking time, luggage and suitable Traveller meeting points when requesting the hire quote."
      },
      {
        "title": "Wedding guests or a Rajasthan circuit",
        "desc": "Provide each Jaipur hotel or venue collection and all onward overnight addresses. Passenger totals and the full schedule help confirm the right Traveller and the charges for waiting, road travel and overnight duties."
      }
    ],
    "nearbyCities": [
      "mathura",
      "agra",
      "new-delhi"
    ],
    "note": "For Jaipur, separate the Amber Palace excursion from your Hawa Mahal, City Palace and shopping stops. Send hotel or arrival details, waiting requirements and any onward Rajasthan stays so access, availability and the complete fare can be confirmed.",
    "intent": "general",
    "tripPlanning": [
      {
        "title": "Jaipur hotel → Amber Palace → Hawa Mahal → City Palace / Jantar Mantar",
        "description": "Plan the Amer excursion alongside the selected old-city monuments. Distinguish a Hawa Mahal photo halt from a full visit and include monument waiting and the hotel return in the vehicle quote."
      },
      {
        "title": "Jaipur → Jodhpur → Jaisalmer Rajasthan itinerary",
        "description": "Provide the hotel nights, sightseeing requests and final drop city. List any Udaipur extension separately so the vehicle duty, bags, waiting and overnight charges reflect the complete journey."
      }
    ]
  },
  {
    "slug": "udaipur",
    "name": "Udaipur",
    "state": "Rajasthan",
    "heroTagline": "Udaipur Taxi Service, Car Hire & Group Traveller Booking",
    "attractions": [
      "City Palace",
      "Lake Pichola",
      "Fateh Sagar Lake",
      "Jagdish Temple"
    ],
    "description": "Arrange Udaipur taxi service and car hire for City Palace, Lake Pichola and hotel transfers. Request cab or Tempo Traveller rental with a complete trip quote.",
    "pickup": "Your Udaipur hotel or road-accessible address, railway arrival point or confirmed airport meeting point",
    "taxiIntro": [
      "Udaipur's City Palace stands beside Lake Pichola, and a sightseeing plan can also include Jagdish Temple and Fateh Sagar Lake. Tell us which palace, temple and lakeside stops you want to combine, with time set aside for walking or a separately arranged boat visit.",
      "A hotel pickup needs the full property name and its vehicle-accessible entrance, especially when your accommodation is around the old city or lakefront. Send flight or train details for an arrival transfer and identify the meeting point for the onward road journey. Any boat transfer should be coordinated separately with its operator.",
      "Request a car for a Udaipur sightseeing day, guest transfers between wedding venues or an onward journey to Jaipur or Jodhpur. List all hotel changes, venue reporting times and the final destination. The team confirms availability, waiting, overnight requirements and the full fare before the booking is final."
    ],
    "tempoUseCases": [
      {
        "title": "City Palace and lakes group sightseeing",
        "desc": "Share the City Palace visit, Lake Pichola meeting point and Fateh Sagar stop in order. Agree where the Traveller waits while the group visits on foot or uses a separately booked boat service."
      },
      {
        "title": "Wedding hotel and venue transfers",
        "desc": "List every Udaipur hotel and venue gate, event reporting time and return collection. Include bags and multiple pickup groups so the selected Traveller's access and duty schedule can be confirmed."
      }
    ],
    "nearbyCities": [
      "jodhpur"
    ],
    "note": "For Udaipur, send the exact hotel entrance and separate road transfers from any lake boat arrangements. Include City Palace waiting, lakeside stops, wedding venue gates and onward overnight stays in the itinerary used for the final quote.",
    "intent": "general",
    "tripPlanning": [
      {
        "title": "Udaipur hotel → City Palace → Jagdish Temple → Fateh Sagar",
        "description": "Send the hotel's vehicle-accessible entrance and the time wanted at each stop. If adding Lake Pichola, identify the road meeting point and coordinate any boat visit separately before agreeing the return collection."
      },
      {
        "title": "Udaipur hotel → Jodhpur stay or Jaipur drop",
        "description": "Choose the onward destination and list all overnight addresses and requested stops. Confirm whether the vehicle finishes there or remains for further touring, with passengers and bags included in the complete quote."
      }
    ]
  },
  {
    "slug": "jodhpur",
    "name": "Jodhpur",
    "state": "Rajasthan",
    "heroTagline": "Jodhpur Taxi, Car Hire & Tempo Traveller Rental",
    "attractions": [
      "Mehrangarh Fort",
      "Jaswant Thada",
      "Umaid Bhawan Palace",
      "Ghanta Ghar"
    ],
    "description": "Book Jodhpur taxi service, cab booking and car hire for Mehrangarh Fort and city travel. Request Tempo Traveller rental for groups and Rajasthan journeys.",
    "pickup": "Your Jodhpur hotel, railway arrival point, airport meeting point or an agreed road-accessible address",
    "taxiIntro": [
      "Mehrangarh Fort overlooks Jodhpur and is a principal stop for a city visit. An itinerary can also include Jaswant Thada, the Umaid Bhawan Palace visitor area and Ghanta Ghar. Specify whether each stop is a visit or a photo halt so waiting is included appropriately.",
      "For a hotel in or around the old city, provide a map pin and confirm the entrance suitable for your selected vehicle. A railway or airport collection needs arrival details, passengers and luggage. Agree where to regroup after fort sightseeing or a market walk before leaving the vehicle.",
      "Jodhpur can be the start, finish or overnight stop on a Rajasthan road journey connecting Jaisalmer, Udaipur and Jaipur. Send the full hotel sequence and any detours with your booking request. The team confirms the car or Traveller, pickup arrangements, billable itinerary and complete quote for your dates."
    ],
    "tempoUseCases": [
      {
        "title": "Mehrangarh and Jodhpur heritage group",
        "desc": "Arrange a common pickup for Mehrangarh, Jaswant Thada and the other agreed city stops. Share expected visit durations and choose accessible parking and meeting points for the group."
      },
      {
        "title": "Jodhpur–Jaisalmer touring transfer",
        "desc": "Provide the Jodhpur hotel pickup and exact Jaisalmer hotel or camp destination, plus intermediate stops. Include bags, overnight plans and the final return city when requesting a Traveller quote."
      }
    ],
    "nearbyCities": [
      "jaisalmer",
      "udaipur"
    ],
    "note": "For Jodhpur sightseeing, distinguish fort visits from short photo or market stops and confirm the hotel's vehicle-accessible entrance. An onward Jaisalmer or Udaipur journey needs all overnight addresses and the final drop included in the quote.",
    "intent": "general",
    "tripPlanning": [
      {
        "title": "Jodhpur hotel → Mehrangarh → Jaswant Thada → Ghanta Ghar",
        "description": "Allow time for the fort visit and market walk, with agreed parking and regrouping points. Add the Umaid Bhawan visitor area explicitly if wanted and confirm the accessible hotel entrance for the return."
      },
      {
        "title": "Jodhpur → Jaisalmer hotel or desert camp",
        "description": "Send the exact destination property, intermediate stops and overnight plan. A camp transfer needs its entrance pin and reporting time; include any onward or return leg in the same road-hire request."
      }
    ]
  },
  {
    "slug": "jaisalmer",
    "name": "Jaisalmer",
    "state": "Rajasthan",
    "heroTagline": "Jaisalmer Taxi, Cab Booking & Tempo Traveller Hire",
    "attractions": [
      "Jaisalmer Fort",
      "Patwon Ki Haveli",
      "Gadisar Lake",
      "Sam sand dunes — separate desert activities"
    ],
    "description": "Arrange Jaisalmer taxi and car hire for the fort, havelis and desert-camp road transfers. Book cab or Tempo Traveller rental with your hotel and camp details.",
    "pickup": "Your Jaisalmer hotel, railway or airport arrival point, or the confirmed road entrance of your desert camp",
    "taxiIntro": [
      "Jaisalmer Fort, Patwon Ki Haveli and Gadisar Lake provide a city sightseeing plan distinct from a desert-camp visit. Decide how much time the group needs at the fort and havelis, and agree the road-accessible pickup and return meeting points for those visits.",
      "For travel towards the Sam or Khuri dunes, send the actual camp name and entrance pin rather than only the desert-area name. Confirm the camp reporting and return times. A taxi or Tempo Traveller quote covers the agreed road transfer; dune driving, camel rides, camp stays and performances are separate arrangements.",
      "Share your train or flight details for an arrival pickup, together with bags and any hotel change before the camp transfer. For onward Jodhpur travel or a longer Rajasthan circuit, provide every overnight stop and the final drop. Vehicle availability, access and the complete road-hire fare are confirmed in advance."
    ],
    "tempoUseCases": [
      {
        "title": "Fort, haveli and Gadisar group visit",
        "desc": "Give the city sightseeing sequence and time needed at each attraction. Confirm where the Traveller can wait and where the group will regroup after exploring the fort or haveli streets on foot."
      },
      {
        "title": "Jaisalmer hotel to desert-camp transfer",
        "desc": "Send the camp's exact entrance pin, group luggage, reporting time and next-day return plan. Book any safari or camp activities separately; request the Traveller for the confirmed road-transfer portion."
      }
    ],
    "nearbyCities": [
      "jodhpur"
    ],
    "note": "For Jaisalmer, distinguish city sightseeing from a Sam or Khuri camp transfer. Provide the camp name, road entrance, reporting time and return plan; safari rides, dune activities and accommodation are separate from the confirmed vehicle quote.",
    "intent": "general",
    "tripPlanning": [
      {
        "title": "Jaisalmer hotel → Fort → Patwon Ki Haveli → Gadisar Lake",
        "description": "Choose visit durations and road-accessible meeting points for the fort and haveli streets. Include walking time and the final hotel collection so the driver duty covers the whole city outing."
      },
      {
        "title": "Jaisalmer hotel → confirmed Sam or Khuri camp → agreed return",
        "description": "Provide the camp name, entrance pin, reporting time and next-day plan. The quote is for the road transfer; camp accommodation, camel rides and dune activities are separate arrangements."
      }
    ]
  },
  {
    "slug": "new-delhi",
    "name": "New Delhi",
    "state": "Delhi",
    "heroTagline": "New Delhi Taxi Service, Car Hire & Tempo Traveller Booking",
    "attractions": [
      "India Gate",
      "Humayun's Tomb",
      "Qutub Minar",
      "Connaught Place"
    ],
    "description": "Book New Delhi taxi service, cab booking and car hire for arrival transfers, meetings and sightseeing. Compare Tempo Traveller rental for your group journey.",
    "pickup": "Your New Delhi address or hotel, the exact railway station exit, or the airport terminal meeting point confirmed for your flight",
    "taxiIntro": [
      "A New Delhi sightseeing booking can include India Gate and Connaught Place, with wider Delhi heritage visits such as Humayun's Tomb and Qutub Minar added to the route. Share the places you want to enter, the photo stops and the time needed for each visit.",
      "Airport and railway pickups require the exact terminal or station printed on your ticket, arrival time, luggage and final address. Give the relevant exit or authorised meeting point when it is confirmed. New Delhi Railway Station and other Delhi stations should be named separately rather than described only as Delhi station.",
      "For business travel, list the office or venue entrances and reporting times along with hotel collections. For an outstation cab or group trip to Mathura, Agra or Jaipur, send every stop and your one-way or return plan. The team confirms vehicle availability, pickup access and the complete fare for the requested itinerary."
    ],
    "tempoUseCases": [
      {
        "title": "Delhi arrival and group sightseeing",
        "desc": "Combine the exact airport terminal or railway-station pickup with your hotel and selected heritage stops. Send the passenger and luggage totals and agree a suitable Traveller meeting point before arrival."
      },
      {
        "title": "New Delhi–Agra–Jaipur itinerary",
        "desc": "List hotel nights, monument stops and the final drop in route order, including Mathura if requested. Confirm waiting, overnight duties and the complete road journey when choosing a Traveller for the group."
      }
    ],
    "nearbyCities": [
      "mathura",
      "agra",
      "jaipur"
    ],
    "note": "For New Delhi, copy the exact airport terminal or railway-station name from your ticket and send the hotel or venue gate. Separate local meetings and sightseeing from any Mathura, Agra or Jaipur leg when confirming pickup, waiting and the full quote.",
    "intent": "general",
    "tripPlanning": [
      {
        "title": "Delhi arrival → hotel → India Gate and Connaught Place",
        "description": "Copy the exact airport terminal or station from the ticket and send the hotel entrance. List the local stops and visit durations, and add Humayun's Tomb or Qutub Minar explicitly if they are part of the road itinerary."
      },
      {
        "title": "New Delhi → Mathura → Agra → Jaipur itinerary",
        "description": "List the chosen temple or monument stops, hotel nights and final drop in route order. Confirm the one-way or return plan and whether the vehicle remains with the group between visits."
      }
    ]
  },
  {
    "slug": "mumbai",
    "name": "Mumbai",
    "state": "Maharashtra",
    "heroTagline": "Mumbai Taxi, Cab Booking & Tempo Traveller Rental",
    "attractions": [
      "Gateway of India",
      "Marine Drive",
      "Chhatrapati Shivaji Maharaj Terminus"
    ],
    "description": "Arrange Mumbai taxi service and car hire for airport or railway pickup, business visits and sightseeing. Request cab or Tempo Traveller rental for your full route.",
    "pickup": "Your exact Mumbai hotel or address, the railway station printed on your ticket, or the confirmed airport terminal meeting point",
    "taxiIntro": [
      "Mumbai's sightseeing landmarks include the Gateway of India, Marine Drive and Chhatrapati Shivaji Maharaj Terminus. Tell us which waterfront or heritage stops you want to combine and whether you need a short visit, time for walking or a return collection after a separately arranged activity.",
      "An arrival transfer needs the exact airport and terminal or railway station shown on your ticket, not only Mumbai. Send the flight or train time, total bags and complete hotel address with its entrance pin. The team confirms the meeting point and pickup plan for the selected vehicle.",
      "For meetings, events or family travel across Mumbai, list every address and reporting time in order. Include any onward outstation journey, overnight stay and final drop in the same request. A car or Tempo Traveller is confirmed against your schedule, access and luggage needs, with the full fare agreed before booking."
    ],
    "tempoUseCases": [
      {
        "title": "Mumbai heritage and waterfront group outing",
        "desc": "Plan Gateway of India, Marine Drive and other selected stops with a common hotel pickup. Specify walking time and return collections, and agree suitable parking or meeting points for the Traveller."
      },
      {
        "title": "Airport, hotel and event transfers",
        "desc": "Send the exact airport terminal, flight details, hotel entrances and event reporting schedule. Include each collection group and luggage total so access, vehicle availability and the complete transfer quote can be confirmed."
      }
    ],
    "nearbyCities": [],
    "note": "For Mumbai, give the exact airport terminal or railway station, neighbourhood and hotel or venue entrance pin. List all collections and reporting times; confirm meeting points, luggage space, waiting and any onward outstation travel in the final quote.",
    "intent": "general",
    "tripPlanning": [
      {
        "title": "Mumbai hotel → Gateway of India → Marine Drive → CSMT",
        "description": "Select the waterfront and heritage stops you want, allowing time for walks and return collections. Agree accessible vehicle meeting points and the final hotel drop; any separate activity needs its own confirmed arrangement."
      },
      {
        "title": "Confirmed Mumbai arrival terminal or station → hotel → event venue",
        "description": "Give the exact arrival location, hotel and venue entrance pins, guest groups and reporting times. Include luggage and the return collection in one transfer schedule so vehicle access, waiting and the complete fare can be confirmed."
      }
    ]
  },
  {
    "slug": "amritsar",
    "name": "Amritsar",
    "state": "Punjab",
    "heroTagline": "Amritsar Taxi Service, Car Hire & Tempo Traveller Rental",
    "attractions": [
      "Golden Temple (Sri Harmandir Sahib)",
      "Jallianwala Bagh",
      "Durgiana Temple",
      "Gobindgarh Fort"
    ],
    "description": "Book Amritsar taxi service and car hire for Golden Temple visits, railway or airport transfers and city travel. Compare cab and Tempo Traveller rental for groups.",
    "pickup": "Your Amritsar hotel or address, Amritsar railway arrival point or the airport meeting point confirmed for your flight",
    "taxiIntro": [
      "Amritsar's principal pilgrimage landmark is the Golden Temple, also known as Sri Harmandir Sahib. A city itinerary can include Jallianwala Bagh, Durgiana Temple and Gobindgarh Fort. Choose your stops and allow time for visits on foot when planning the vehicle duty.",
      "For a hotel, airport or railway pickup, send the exact arrival details and accommodation address. Agree the accessible drop and return meeting point for the Golden Temple area, especially when travelling with elderly passengers or larger bags. Tell the team about boarding and luggage needs before choosing the vehicle.",
      "A car or Tempo Traveller can be requested for Amritsar sightseeing, family gatherings, wedding guests or an onward road journey. Give every pickup, event time, overnight stay and final destination. Availability, waiting, access arrangements and the complete hire quote are confirmed for your travel dates."
    ],
    "tempoUseCases": [
      {
        "title": "Golden Temple and Amritsar group visit",
        "desc": "Share the hotel collections and the Golden Temple, Jallianwala Bagh or other requested stops. Agree parking and regrouping points, with time for the group to complete each visit before the next transfer."
      },
      {
        "title": "Family arrivals and wedding transport",
        "desc": "List flight or train arrivals, hotel entrances, wedding venues and return collection times. Passenger groups and luggage totals help the team confirm the Traveller and quote the full event-transfer schedule."
      }
    ],
    "nearbyCities": [],
    "note": "For Amritsar, send arrival details, hotel or venue entrances and the planned Golden Temple meeting point. Include walking time, group luggage and all return collections when confirming the vehicle, waiting arrangements and complete fare.",
    "intent": "pilgrimage",
    "tripPlanning": [
      {
        "title": "Amritsar hotel → Golden Temple → Jallianwala Bagh → hotel",
        "description": "Agree the road-accessible drop and regrouping point for the Golden Temple area. Allow time for visits on foot and share any boarding or luggage needs before selecting the car or Traveller."
      },
      {
        "title": "Amritsar arrival → Durgiana Temple → Gobindgarh Fort → hotel",
        "description": "Send the exact train or flight details and accommodation address, then choose the visit order and waiting. Confirm whether these stops follow the arrival directly or form a separate sightseeing duty during the stay."
      }
    ]
  },
  {
    "slug": "omkareshwar",
    "name": "Omkareshwar",
    "state": "Madhya Pradesh",
    "heroTagline": "Omkareshwar Taxi, Car Hire & Tempo Traveller Booking",
    "attractions": [
      "Omkareshwar Jyotirlinga Temple",
      "Mamleshwar Temple",
      "Narmada riverfront",
      "Ekatma Dham"
    ],
    "description": "Book Omkareshwar taxi service and car hire for Jyotirlinga darshan, Mamleshwar and Narmada visits. Request cab or Tempo Traveller rental for your pilgrimage.",
    "pickup": "Your Omkareshwar hotel, agreed road-accessible temple meeting point or the starting address for your transfer",
    "taxiIntro": [
      "Omkareshwar is a Narmada pilgrimage destination centred on the Omkareshwar Jyotirlinga Temple. A visit can also include Mamleshwar Temple and Ekatma Dham. List the places you want to see and the time needed for darshan or a riverfront walk, so the vehicle duty covers the whole plan.",
      "Send your hotel or starting address and agree where the vehicle will drop you before the temple visit. The return collection should have a clear meeting point and time. Mention walking or boarding needs when choosing a car, SUV or Traveller; any boat trip or religious arrangements are separate from the road transfer.",
      "An Omkareshwar cab booking can be a same-day return from Indore, a hotel transfer or part of an Ujjain and Maheshwar itinerary. Share all overnight stops, passengers, luggage and the final drop. The team confirms the selected vehicle, pickup arrangements, waiting and complete fare for your dates."
    ],
    "tempoUseCases": [
      {
        "title": "Omkareshwar and Mamleshwar group darshan",
        "desc": "Provide a common pickup and the order of temple visits. Agree accessible parking and regrouping points, allowing time for darshan and the group's walk back before the onward road journey."
      },
      {
        "title": "Ujjain–Omkareshwar–Maheshwar pilgrimage",
        "desc": "Send the full temple circuit, hotel addresses and final return city. Include bags and overnight duties when requesting a Traveller quote; temple services and optional river activities are arranged separately."
      }
    ],
    "nearbyCities": [
      "indore",
      "maheshwar",
      "ujjain"
    ],
    "note": "For Omkareshwar, list the Jyotirlinga, Mamleshwar and any Ekatma Dham stops separately. Agree road-accessible drop and return meeting points, waiting and overnight stays; include the full Indore, Ujjain or Maheshwar connection in your fare request.",
    "intent": "pilgrimage",
    "tripPlanning": [
      {
        "title": "Indore → Omkareshwar Jyotirlinga → Mamleshwar → Indore",
        "description": "List the temple visits and any Ekatma Dham stop before requesting the return itinerary. Agree road-accessible drops, darshan waiting and regrouping points; boat trips and religious arrangements are separate."
      },
      {
        "title": "Ujjain → Omkareshwar → Maheshwar itinerary",
        "description": "Provide the full pilgrimage sequence, hotel nights and final city drop. Include passengers, luggage and whether the vehicle waits through the visits so the complete multi-stop road duty can be quoted."
      }
    ]
  },
  {
    "slug": "pachmarhi",
    "name": "Pachmarhi",
    "state": "Madhya Pradesh",
    "heroTagline": "Pachmarhi Taxi Service, Car Hire & Tempo Traveller Rental",
    "attractions": [
      "Bee Falls",
      "Dhoopgarh",
      "Jata Shankar Caves",
      "Pandava Caves"
    ],
    "description": "Arrange Pachmarhi taxi service and car hire for hotel transfers and a planned hill-station visit. Request cab or Tempo Traveller rental with sightseeing access confirmed.",
    "pickup": "Your Pachmarhi hotel, Pipariya Railway Station meeting point or an agreed starting address for the hill transfer",
    "taxiIntro": [
      "Pachmarhi's Satpura setting offers waterfall, cave and viewpoint visits, including Bee Falls, Dhoopgarh, Jata Shankar and the Pandava Caves. Select the stops that suit your group and allow time for walking rather than treating every attraction as a quick roadside drop.",
      "For a rail arrival, send the Pipariya station details, train time and Pachmarhi hotel address. Distinguish the hotel road transfer from the local sightseeing plan. Before confirming each excursion, check current access, the vehicle permitted on that route and whether separate local transport, entry or guide arrangements are needed.",
      "Request a car or Tempo Traveller for your hotel transfer, family holiday or a wider trip connected with Bhopal or Jabalpur. Share overnight stays, luggage and the return train or final destination. Vehicle suitability, pickup timing, waiting and the full road-hire quote are confirmed against the agreed itinerary."
    ],
    "tempoUseCases": [
      {
        "title": "Pipariya arrival and Pachmarhi hotel transfer",
        "desc": "Give the train details, group luggage and hotel entrance pin. Confirm the Traveller's access and the return station collection as part of one transfer schedule."
      },
      {
        "title": "Family hill-station stay with planned excursions",
        "desc": "List Bee Falls, Dhoopgarh and other requested visits with walking and regrouping time. Confirm which road legs suit the selected Traveller and arrange any required local excursion transport separately."
      }
    ],
    "nearbyCities": [
      "satpura",
      "bhopal",
      "jabalpur"
    ],
    "note": "For Pachmarhi, provide Pipariya train details if arriving by rail and your exact hotel. Separate the hill transfer from each sightseeing excursion; confirm current route access, suitable vehicles, walking needs and any separate local arrangements before the full fare is agreed.",
    "intent": "general",
    "tripPlanning": [
      {
        "title": "Pipariya Railway Station → Pachmarhi hotel → Pipariya return",
        "description": "Send both train connections, the hotel entrance and all bags. Confirm the hill transfer and return collection separately from any sightseeing excursions included during the stay."
      },
      {
        "title": "Pachmarhi hotel → chosen Bee Falls / Dhoopgarh excursion → hotel",
        "description": "Choose the actual stops, allowing walking time rather than assuming roadside access. Check current route permissions and the vehicle suitable for each excursion; arrange any required local transport, entry or guide separately."
      }
    ]
  },
  {
    "slug": "vrindavan",
    "name": "Vrindavan",
    "state": "Uttar Pradesh",
    "heroTagline": "Vrindavan Taxi, Cab Booking & Tempo Traveller Hire",
    "attractions": [
      "Banke Bihari Temple",
      "Prem Mandir",
      "ISKCON Temple",
      "Radha Raman Temple"
    ],
    "description": "Book Vrindavan taxi service and car hire for Banke Bihari, Prem Mandir and other temple visits. Compare cab booking and Tempo Traveller rental for your group.",
    "pickup": "Your Vrindavan hotel or ashram, an agreed parking meeting point or Mathura Junction for an onward transfer",
    "taxiIntro": [
      "Vrindavan has several distinct temple stops, including Banke Bihari Temple, Prem Mandir, ISKCON Temple and Radha Raman Temple. Tell us which visits you want to combine and how long the group expects to stay, so the road transfers and waiting match your darshan plan.",
      "A Vrindavan hotel or ashram pickup needs the full property name, entrance pin and a suitable place to board. For arrival at Mathura Junction, send the train number and onward Vrindavan address. Agree parking and walking meeting points for the temple visits, especially when selecting a larger Traveller.",
      "You can request a Vrindavan-only itinerary, a combined Mathura and Vrindavan visit or an onward journey to Agra or New Delhi. Include each temple, hotel change, bag count and final drop. Availability, vehicle access, return collections and the complete fare are confirmed before booking."
    ],
    "tempoUseCases": [
      {
        "title": "Banke Bihari and Prem Mandir group visit",
        "desc": "List the temples in your preferred order and choose a common hotel or ashram pickup. Agree parking, visit duration and a clear regrouping point for every transfer."
      },
      {
        "title": "Mathura Junction and Braj family itinerary",
        "desc": "Share the train arrival, Vrindavan accommodation and any Mathura temple stops. Include luggage, overnight plans and the final station or city drop when requesting the Traveller quote."
      }
    ],
    "nearbyCities": [
      "mathura",
      "agra"
    ],
    "note": "For Vrindavan, name each temple and the exact hotel or ashram entrance. Keep Mathura Junction pickup and Mathura sightseeing as separate itinerary stops; agree suitable parking, walking meeting points and return timings before confirming the full quote.",
    "intent": "pilgrimage",
    "tripPlanning": [
      {
        "title": "Mathura Junction → Vrindavan hotel → Banke Bihari Temple → Prem Mandir",
        "description": "Provide the train arrival and hotel or ashram entrance, then list temple waiting and the final drop. Agree parking and walking meeting points for the chosen vehicle before combining the transfers."
      },
      {
        "title": "Vrindavan hotel → ISKCON Temple → Radha Raman Temple → onward Mathura",
        "description": "Choose the visit order and identify any Mathura sightseeing or station drop separately. Include return collections, bags and hotel changes so access and the complete road fare can be confirmed."
      }
    ]
  },
  {
    "slug": "haridwar",
    "name": "Haridwar",
    "state": "Uttarakhand",
    "heroTagline": "Haridwar Taxi Service, Car Hire & Tempo Traveller Booking",
    "attractions": [
      "Har Ki Pauri",
      "Mansa Devi Temple",
      "Chandi Devi Temple"
    ],
    "description": "Arrange Haridwar taxi service and car hire for Har Ki Pauri, temple visits and railway transfers. Request cab or Tempo Traveller rental with pickup and waiting agreed.",
    "pickup": "Haridwar Railway Station, your hotel or dharamshala, or an agreed accessible road meeting point",
    "taxiIntro": [
      "Haridwar visits often centre on Har Ki Pauri on the Ganga, with Mansa Devi and Chandi Devi temples added to the plan. Specify whether you want a short ghat visit, time for aarti or a longer temple itinerary, so the requested vehicle duty includes the necessary waiting.",
      "Send the exact hotel or dharamshala entrance, or the train details for a Haridwar station pickup. Agree an accessible road drop and the return meeting point before leaving for the ghats or temples. Temple access and any ropeway arrangements should be coordinated separately from the booked road transport.",
      "A Haridwar cab or Traveller can be requested for local travel, family pilgrimage or an onward Rishikesh stay. List every pickup, accommodation address and final departure connection. The team confirms suitable vehicle access, passenger and luggage capacity, waiting and the complete fare in advance."
    ],
    "tempoUseCases": [
      {
        "title": "Har Ki Pauri and temple family group",
        "desc": "Provide the hotel collections and your planned ghat or temple visits. Include time for aarti or darshan, then agree the Traveller's parking and regrouping point for the return journey."
      },
      {
        "title": "Haridwar railway arrival with Rishikesh stay",
        "desc": "Send the train details, Haridwar stops and full Rishikesh hotel or ashram address. Include bags and your final return connection so the group road transfers can be quoted together."
      }
    ],
    "nearbyCities": [
      "rishikesh"
    ],
    "note": "For Haridwar, send train details or the exact hotel entrance and distinguish ghat waiting from temple visits. Agree accessible pickup points and return collections; include any Rishikesh stay while arranging ropeway or temple services separately.",
    "intent": "pilgrimage",
    "tripPlanning": [
      {
        "title": "Haridwar station or hotel → Har Ki Pauri → agreed return",
        "description": "Specify whether the ghat visit includes aarti and how much waiting is needed. Send the exact hotel entrance or train arrival and agree a road-accessible drop and regrouping point for the return."
      },
      {
        "title": "Haridwar → selected Mansa Devi / Chandi Devi visits → Rishikesh stay",
        "description": "List the requested temples and full Rishikesh hotel or ashram address. Coordinate temple access and any ropeway separately, then confirm the vehicle meeting points, luggage and onward drop."
      }
    ]
  },
  {
    "slug": "rishikesh",
    "name": "Rishikesh",
    "state": "Uttarakhand",
    "heroTagline": "Rishikesh Taxi, Car Hire & Tempo Traveller Rental",
    "attractions": [
      "Triveni Ghat",
      "Beatles Ashram (Chaurasi Kutia)",
      "Ganga riverfront"
    ],
    "description": "Book Rishikesh taxi service and car hire for ashram stays, Triveni Ghat and arrival transfers. Compare cab and Tempo Traveller rental for your complete itinerary.",
    "pickup": "Your exact Rishikesh hotel, ashram or retreat entrance, or the confirmed railway or airport arrival meeting point",
    "taxiIntro": [
      "Rishikesh combines Ganga riverfront visits, ashram stays and sightseeing places such as Triveni Ghat and Beatles Ashram, also called Chaurasi Kutia. Decide which visits belong in your road itinerary and how much time you need on foot or at a retreat session.",
      "The full hotel, ashram or retreat address matters for a Rishikesh transfer: send the entrance pin and confirm the vehicle approach with your accommodation. For a railway or airport pickup, copy the exact arrival location from your ticket. Agree the road meeting point rather than assuming every riverside property has the same access.",
      "Request a cab or Traveller for arrival and departure transfers, group sightseeing or a combined Haridwar and Rishikesh stay. Give class or event reporting times, bags and all overnight addresses. The team confirms the road-hire plan and full fare; rafting, yoga sessions, guided visits and other activities are separate arrangements."
    ],
    "tempoUseCases": [
      {
        "title": "Yoga or retreat group arrival",
        "desc": "Share the exact retreat entrance, arrival location, session reporting time and luggage count. Confirm access for the selected Traveller and the departure collection before finalising the group transfer."
      },
      {
        "title": "Rishikesh riverfront and Haridwar visit",
        "desc": "List Triveni Ghat, the requested ashram or sightseeing stops and any Haridwar leg. Agree road-accessible regrouping points and waiting, with separate bookings for activities beyond vehicle hire."
      }
    ],
    "nearbyCities": [
      "haridwar"
    ],
    "note": "For Rishikesh, provide the hotel or ashram entrance pin, confirmed approach and exact arrival station or airport. Include retreat timings, riverfront waiting and any Haridwar connection; activity bookings and road transport are confirmed separately.",
    "intent": "pilgrimage",
    "tripPlanning": [
      {
        "title": "Rishikesh accommodation → Triveni Ghat → Beatles Ashram → return",
        "description": "Share the accessible hotel or retreat entrance and time needed for walking or visits. Agree road regrouping points and waiting; guided visits, sessions and other activities are arranged separately."
      },
      {
        "title": "Haridwar arrival or stay → Rishikesh hotel / ashram → departure collection",
        "description": "Send the exact arrival location, retreat entrance pin and reporting schedule. Include bags, all hotel nights and the final departure connection so approach access and the full group transfer plan can be confirmed."
      }
    ]
  },
  {
    "slug": "ajmer",
    "name": "Ajmer",
    "state": "Rajasthan",
    "heroTagline": "Ajmer Taxi Service, Cab Booking & Tempo Traveller Hire",
    "attractions": [
      "Ajmer Sharif Dargah",
      "Ana Sagar Lake",
      "Adhai Din Ka Jhonpra",
      "Soniji Ki Nasiyan"
    ],
    "description": "Book Ajmer taxi service and car hire for Dargah visits, Ana Sagar and railway transfers. Request cab or Tempo Traveller rental for Ajmer and Pushkar travel.",
    "pickup": "Ajmer Junction, your Ajmer hotel or address, or an agreed road-accessible meeting point",
    "taxiIntro": [
      "Ajmer Sharif Dargah is the shrine of the Sufi saint Khwaja Moinuddin Chishti and a major focus of visits to Ajmer. A city plan can also include Ana Sagar Lake, Adhai Din Ka Jhonpra and Soniji Ki Nasiyan. Name the stops you want rather than requesting an unspecified city tour.",
      "For a Dargah visit, agree the vehicle's accessible drop point and where the driver will collect you afterwards. Send your hotel entrance or Ajmer Junction train details, group luggage and any walking assistance needs. Include the time you expect to spend at the shrine and other stops in your booking request.",
      "An Ajmer car or Tempo Traveller booking can include a Pushkar visit, a Jaipur connection or several days of Rajasthan travel. Provide the full stop order, overnight addresses and final drop. The team confirms vehicle suitability, pickup, waiting and the complete fare for the agreed road journey."
    ],
    "tempoUseCases": [
      {
        "title": "Ajmer Dargah and city group visit",
        "desc": "Share the hotel or station pickup and the requested Dargah, lake or heritage stops. Allow time for the group to walk back to the agreed parking meeting point before the next road transfer."
      },
      {
        "title": "Ajmer–Pushkar with a Jaipur connection",
        "desc": "List both cities' visits, all hotel collections and the final Jaipur or Ajmer drop. Include passengers, luggage and overnight duties when requesting a Traveller and the complete itinerary quote."
      }
    ],
    "nearbyCities": [
      "pushkar",
      "jaipur"
    ],
    "note": "For Ajmer, agree the Dargah drop and return meeting points and send the exact station or hotel details. Keep Pushkar visits and any Jaipur transfer in the written itinerary so waiting, luggage, overnight duties and the full fare can be confirmed.",
    "intent": "pilgrimage",
    "tripPlanning": [
      {
        "title": "Ajmer Junction → Ajmer Sharif Dargah → Ana Sagar → hotel",
        "description": "Give the train arrival and hotel address, then allow time for the shrine visit and lake stop. Agree the Dargah's accessible road drop and return meeting point before confirming waiting and the final fare."
      },
      {
        "title": "Ajmer → Pushkar visit → Jaipur connection",
        "description": "Name the requested Pushkar visits and all hotel collections, then specify the final Jaipur address or return to Ajmer. Include overnight stays and luggage in the itinerary used to confirm the vehicle and full duty."
      }
    ]
  },
  {
    "slug": "pushkar",
    "name": "Pushkar",
    "state": "Rajasthan",
    "heroTagline": "Pushkar Taxi, Car Hire & Tempo Traveller Booking",
    "attractions": [
      "Brahma Temple",
      "Pushkar Lake",
      "Savitri Temple",
      "Varaha Temple"
    ],
    "description": "Arrange Pushkar taxi service and car hire for Brahma Temple, lake visits and hotel transfers. Book cab or Tempo Traveller rental with Ajmer connections included.",
    "pickup": "Your Pushkar hotel or resort, an agreed accessible town meeting point or Ajmer Junction for an onward transfer",
    "taxiIntro": [
      "Pushkar's pilgrimage stops include Brahma Temple and the ghats around Pushkar Lake, with Savitri Temple and Varaha Temple among other possible visits. Choose the temples and lake stops you want to combine, and include time for walking or independently arranged religious activities.",
      "For a resort or hotel pickup, send the property entrance pin as well as its name. If arriving through Ajmer Junction, share the train details and the onward Pushkar address. Agree vehicle parking and return meeting points for the town visits; any ropeway or activity tickets are separate from road hire.",
      "Request local car hire, an Ajmer–Pushkar return trip or a road journey continuing to Jaipur. For a fair, wedding or resort event, include the exact venue gate and reporting schedule rather than only Pushkar. Vehicle availability, suitable access, waiting and the complete fare are confirmed for your dates."
    ],
    "tempoUseCases": [
      {
        "title": "Brahma Temple and Pushkar Lake group",
        "desc": "List the temple and ghat visits with a common hotel or Ajmer pickup. Agree a suitable Traveller parking point and enough time for everyone to return from the walking portion of the visit."
      },
      {
        "title": "Resort, wedding or fair group transfers",
        "desc": "Send the exact property or event gate, guest collections and return times. Include luggage and any Ajmer station connection so the selected Traveller's access and full duty can be confirmed."
      }
    ],
    "nearbyCities": [
      "ajmer",
      "jaipur"
    ],
    "note": "For Pushkar, give the hotel or event entrance pin and list Brahma Temple, lake and other visits separately. Agree parking and regrouping points; include Ajmer rail transfers, waiting and any Jaipur leg in the complete quote request.",
    "intent": "pilgrimage",
    "tripPlanning": [
      {
        "title": "Ajmer Junction → Pushkar hotel → Brahma Temple → Pushkar Lake",
        "description": "Send the train details and property entrance, then agree parking and time for the temple and ghat walks. Confirm the return meeting point and whether the vehicle finishes at the hotel or returns to Ajmer."
      },
      {
        "title": "Pushkar hotel → chosen Savitri / Varaha Temple visits → Jaipur drop",
        "description": "List the temples actually requested and the final Jaipur address. Keep any ropeway or activity ticket separate from road hire, and agree access, visit waiting and luggage before the complete quote is confirmed."
      }
    ]
  },
  {
    "slug": "mount-abu",
    "name": "Mount Abu",
    "state": "Rajasthan",
    "heroTagline": "Mount Abu Taxi Service, Car Hire & Tempo Traveller Rental",
    "attractions": [
      "Nakki Lake",
      "Dilwara Jain Temples",
      "Guru Shikhar",
      "Toad Rock viewpoint"
    ],
    "description": "Book Mount Abu taxi service and car hire for hill-station stays, Nakki Lake and temple visits. Request cab or Tempo Traveller rental with Abu Road transfers.",
    "pickup": "Your Mount Abu hotel, Abu Road Railway Station meeting point or an agreed starting address for the hill transfer",
    "taxiIntro": [
      "Mount Abu is a Rajasthan hill-station destination with Nakki Lake, the Dilwara Jain Temples, Guru Shikhar and the Toad Rock viewpoint among its attractions. Select the visits that suit the group and allow for steps, walking and time away from the vehicle at the viewpoints or temples.",
      "Abu Road Railway Station pickup and Mount Abu hotel pickup are different transfer requests. Send the station details from your ticket and the full hill-station hotel address. Confirm vehicle access and the meeting points for sightseeing, particularly when travelling with a group, elderly passengers or larger luggage.",
      "A car or Tempo Traveller can be requested for the hill transfer, a local itinerary or a longer trip linked with Udaipur. Include hotel nights, sightseeing waiting and the final station or city drop. The team confirms the suitable vehicle, pickup arrangement and complete road-hire fare; entry tickets and lake activities are separate."
    ],
    "tempoUseCases": [
      {
        "title": "Abu Road arrival and Mount Abu stay",
        "desc": "Share the train time, group luggage and hill-station hotel entrance. Confirm the Traveller's route access, hotel reporting and return station collection as one transfer plan."
      },
      {
        "title": "Nakki Lake, Dilwara and viewpoint outing",
        "desc": "List the sightseeing stops and allow time for walks or temple visits. Agree where the Traveller can wait, suitable regrouping points and whether the trip continues to an Udaipur hotel."
      }
    ],
    "nearbyCities": [
      "udaipur"
    ],
    "note": "For Mount Abu, distinguish Abu Road station arrival from a pickup already in the hill station. Send the hotel entrance, chosen sightseeing stops and walking needs; confirm route access, waiting and any Udaipur connection before agreeing the full fare.",
    "intent": "general",
    "tripPlanning": [
      {
        "title": "Abu Road Railway Station → Mount Abu hotel → Abu Road return",
        "description": "Send both train details and the actual hill-station hotel entrance. Confirm the vehicle's approach, bags and return collection so the hill transfer is distinct from a pickup already within Mount Abu."
      },
      {
        "title": "Mount Abu hotel → Dilwara Temples → Nakki Lake → selected viewpoint",
        "description": "Choose Guru Shikhar or Toad Rock explicitly and allow for steps and walking. Agree vehicle waiting and regrouping points; any lake activity is separate, and an onward Udaipur leg must be included in the quote request."
      }
    ]
  },
  {
    "slug": "old-delhi",
    "name": "Old Delhi",
    "state": "Delhi",
    "heroTagline": "Old Delhi Taxi, Cab Booking & Tempo Traveller Hire",
    "attractions": [
      "Red Fort",
      "Jama Masjid",
      "Chandni Chowk",
      "Gurudwara Sis Ganj Sahib"
    ],
    "description": "Arrange Old Delhi taxi service, car hire and Tempo Traveller transfers for Red Fort, Jama Masjid and Chandni Chowk visits, with suitable pickup points agreed.",
    "pickup": "Your Old Delhi hotel or address, Old Delhi Railway Station exit, or an agreed road-accessible meeting point",
    "taxiIntro": [
      "Old Delhi, the historic Shahjahanabad area, brings together the Red Fort, Jama Masjid and Chandni Chowk markets. A useful itinerary separates monument visits from a market walk or shopping collection, with time to return to a meeting point that the chosen vehicle can reach.",
      "For station pickup, distinguish Old Delhi Railway Station from New Delhi Railway Station and send the exact station, train time and exit shown in your arrival plan. Share your hotel or shop entrance pin. For Chandni Chowk and surrounding lanes, agree road access and any walking or separately arranged local transport before the cab is dispatched.",
      "Choose a sedan for a smaller party with light luggage, an MPV or SUV for family travel, or a Tempo Traveller for a group meeting at an accessible pickup point. A booking can link Old Delhi with New Delhi sightseeing or an onward Agra or Jaipur journey. Send all stops, bags and the final drop so availability, waiting and the complete fare can be confirmed."
    ],
    "tempoUseCases": [
      {
        "title": "Red Fort and Old Delhi heritage group",
        "desc": "Combine a Red Fort visit with Jama Masjid or a Chandni Chowk walking stop. Choose an accessible group drop and collection point and include the time needed to regroup before the next road transfer."
      },
      {
        "title": "Railway arrival and Delhi hotel transfer",
        "desc": "Send the exact station name, train arrival, group contact and hotel entrance. Include total passengers and bags, then confirm the Traveller meeting point and any onward airport or outstation leg."
      }
    ],
    "tripPlanning": [
      {
        "title": "Hotel → Red Fort → Jama Masjid → Chandni Chowk meeting point",
        "description": "Plan monument entry and the market walking portion separately. Agree where the car or Traveller waits and the final return collection."
      },
      {
        "title": "Old Delhi Railway Station → New Delhi hotel or airport",
        "description": "Specify the station exit and final hotel entrance or flight terminal. A one-way transfer and a vehicle retained for additional stops need different duty plans."
      },
      {
        "title": "Old Delhi → Agra or Jaipur",
        "description": "Give the departure point, optional sightseeing stops and final hotel. Confirm whether the vehicle returns with you or finishes at the onward destination."
      }
    ],
    "nearbyCities": [
      "new-delhi",
      "mathura",
      "agra"
    ],
    "note": "For Old Delhi, name the exact railway station and agree a vehicle-accessible hotel, market or monument meeting point. Separate walking time from road transfers and include luggage, waiting and the final drop before confirming your quote.",
    "intent": "general"
  },
  {
    "slug": "jhansi",
    "name": "Jhansi",
    "state": "Uttar Pradesh",
    "heroTagline": "Jhansi Taxi Service, Car Hire & Tempo Traveller Rental",
    "attractions": [
      "Jhansi Fort",
      "Rani Mahal",
      "Government Museum, Jhansi"
    ],
    "description": "Book Jhansi taxi service and car hire for fort visits, railway pickup and Orchha transfers. Compare cab booking and Tempo Traveller rental for your itinerary.",
    "pickup": "Virangana Lakshmibai Jhansi railway station, your Jhansi hotel or an agreed city address",
    "taxiIntro": [
      "Jhansi Fort and Rani Mahal provide distinct stops for a visit focused on Rani Lakshmibai and Bundelkhand heritage. The Government Museum can be added to the same plan. List which sites you want to enter and the time for each visit, rather than treating a heritage outing as one quick city drop.",
      "For a rail arrival, send the station name exactly as printed on your ticket, train number, luggage and final hotel. An onward Orchha transfer should identify the actual hotel or temple meeting point. If sightseeing is planned before check-in, include where bags will remain and the waiting needed at Jhansi Fort or Rani Mahal.",
      "A family can compare cars, MPVs and SUVs, while a wedding party or heritage group can request a Tempo Traveller with suitable luggage space. Jhansi can also connect an Orchha stay with Khajuraho, Gwalior or Chitrakoot. Share the complete stop order, nights and final departure connection for an availability check and full fare."
    ],
    "tempoUseCases": [
      {
        "title": "Jhansi arrival with an Orchha group stay",
        "desc": "Send train details and one group contact, then list the Orchha hotel, any temple stop and the return collection. Include bags and the time needed to board at the agreed station meeting point."
      },
      {
        "title": "Bundelkhand heritage journey",
        "desc": "Plan Jhansi Fort and Rani Mahal alongside the requested Orchha or Khajuraho visits. Give every overnight address and final drop so Traveller duty, waiting and the complete road journey can be quoted."
      }
    ],
    "tripPlanning": [
      {
        "title": "Jhansi railway station → Jhansi Fort → Orchha hotel",
        "description": "Allow time for the fort visit between the train arrival and hotel check-in. Confirm the station exit, luggage plan and onward hotel entrance."
      },
      {
        "title": "Jhansi → Orchha → Khajuraho",
        "description": "List the Orchha monuments and any overnight stay before the Khajuraho transfer. Confirm one-way or return hire for the complete itinerary."
      },
      {
        "title": "Jhansi hotel → Rani Mahal → Government Museum → station",
        "description": "Share the train departure and desired visit durations so the return station transfer is planned with the sightseeing duty."
      }
    ],
    "nearbyCities": [
      "orchha",
      "gwalior",
      "khajuraho"
    ],
    "note": "For Jhansi, send the exact railway arrival or hotel entrance and separate fort or museum waiting from an Orchha transfer. Include luggage, all overnight stops and the final station or city drop when confirming the vehicle and fare.",
    "intent": "general"
  },
  {
    "slug": "vindhyachal",
    "name": "Vindhyachal",
    "state": "Uttar Pradesh",
    "heroTagline": "Vindhyachal Taxi, Car Hire & Tempo Traveller Booking",
    "attractions": [
      "Maa Vindhyavasini Temple",
      "Kali Khoh Temple",
      "Ashtabhuja Temple"
    ],
    "description": "Arrange Vindhyachal taxi service and car hire for Vindhyavasini, Kali Khoh and Ashtabhuja visits. Request cab or Tempo Traveller rental for your darshan plan.",
    "pickup": "Your Vindhyachal hotel, the exact Vindhyachal or Mirzapur railway arrival point, or an agreed accessible meeting point",
    "taxiIntro": [
      "Vindhyachal in Mirzapur district is known for Maa Vindhyavasini Temple. Kali Khoh and Ashtabhuja are separate stops in the Trikon pilgrimage circuit. Name each temple you want to visit and allow time for darshan and the walking portion at every stop.",
      "Vindhyachal and Mirzapur railway pickups need different meeting instructions, so copy the station name from your ticket and send the arrival time. For a temple collection, agree the road-accessible parking or regrouping point and the return time. Tell the team about boarding needs before choosing a car, MPV or larger Traveller.",
      "A Vindhyachal itinerary can be a local return journey or part of a pilgrimage linked with Varanasi or Prayagraj. Share all hotels, passengers, bags and religious stops, including where the road booking should finish. Vehicle suitability, waiting and the full fare are confirmed in advance; religious services and other temple arrangements are separate."
    ],
    "tempoUseCases": [
      {
        "title": "Vindhyavasini, Kali Khoh and Ashtabhuja group darshan",
        "desc": "Provide the temple sequence and expected time at each stop. Agree parking and a common group meeting point, including any walking or boarding assistance needs."
      },
      {
        "title": "Varanasi or Prayagraj pilgrimage connection",
        "desc": "Send the starting hotel, Vindhyachal visits and final drop city. Include overnight stays and bags when requesting the Traveller, with waiting and road travel quoted together."
      }
    ],
    "tripPlanning": [
      {
        "title": "Vindhyachal pickup → Vindhyavasini → Kali Khoh → Ashtabhuja",
        "description": "Request the three temple stops explicitly and agree accessible drop and regrouping points. Darshan duration and return timing are part of the duty plan."
      },
      {
        "title": "Varanasi hotel → Vindhyachal darshan → Varanasi return",
        "description": "Give the hotel entrance, selected temples and planned return. Confirm whether the car remains on duty throughout the visits."
      },
      {
        "title": "Mirzapur railway station → Vindhyachal → Prayagraj",
        "description": "Share the exact station arrival, bags and onward hotel or meeting point. Include all intermediate temple visits when requesting a one-way quote."
      }
    ],
    "nearbyCities": [
      "varanasi",
      "prayagraj"
    ],
    "note": "For Vindhyachal, distinguish Vindhyachal station from Mirzapur station and name all requested temples. Agree parking, walking meeting points, waiting and any Varanasi or Prayagraj leg before confirming the complete road-hire quote.",
    "intent": "pilgrimage"
  },
  {
    "slug": "gorakhpur",
    "name": "Gorakhpur",
    "state": "Uttar Pradesh",
    "heroTagline": "Gorakhpur Taxi Service, Cab Booking & Tempo Traveller Hire",
    "attractions": [
      "Gorakhnath Temple",
      "Ramgarh Tal",
      "Vishnu Temple, Asuran Chowk"
    ],
    "description": "Book Gorakhpur taxi service and car hire for Gorakhnath Temple, Ramgarh Tal and arrival transfers. Request cab or Tempo Traveller rental with driver.",
    "pickup": "Gorakhpur railway arrival point, the airport meeting point for your flight, or your exact hotel or city address",
    "taxiIntro": [
      "Gorakhpur sightseeing can combine Gorakhnath Temple with Ramgarh Tal, while Vishnu Temple at Asuran Chowk is another possible city stop. A temple visit and a lakeside outing have different waiting needs, so tell us what you want to include and the time you intend to spend away from the vehicle.",
      "Railway and airport collections need the arrival details, passenger count, luggage and the complete hotel or home address. For meetings or family functions, share the actual venue gate rather than only a neighbourhood name. Agree the pickup sequence when the party is split across several hotels.",
      "Choose a sedan, MPV or SUV for a smaller family or business itinerary and compare Tempo Traveller options for a larger party. An onward Ayodhya or Varanasi journey can be included as a one-way drop or return hire. Send every overnight stop and the final destination so vehicle availability, waiting and the complete fare are confirmed for your dates."
    ],
    "tempoUseCases": [
      {
        "title": "Gorakhnath Temple and Ramgarh Tal family outing",
        "desc": "Plan a common pickup and list the temple and lakeside stops. Include time for darshan or a walk, then agree an accessible regrouping point for the whole party."
      },
      {
        "title": "Railway arrivals and onward Ayodhya travel",
        "desc": "Provide each train arrival, one group contact and the final Ayodhya hotel or drop. Include bags and all additional Gorakhpur collections so the Traveller and duty schedule can be confirmed."
      }
    ],
    "tripPlanning": [
      {
        "title": "Gorakhpur hotel → Gorakhnath Temple → Ramgarh Tal → hotel",
        "description": "Specify visit durations and the desired lakeside collection time. Confirm whether there are extra family or shopping pickups along the way."
      },
      {
        "title": "Gorakhpur railway station or airport → city hotel",
        "description": "Send the exact arrival details and hotel entrance with bags and passengers. Add any meeting or temple stop before requesting the transfer fare."
      },
      {
        "title": "Gorakhpur → Ayodhya or Varanasi",
        "description": "State the final drop city and whether the journey is one-way or a round trip. Include hotel nights and sightseeing waiting in the itinerary."
      }
    ],
    "nearbyCities": [
      "ayodhya"
    ],
    "note": "For Gorakhpur, provide train or flight details, the exact hotel or venue gate and each requested Gorakhnath Temple or Ramgarh Tal stop. Include the final onward city, passengers, luggage and return plan in your complete quote request.",
    "intent": "general"
  },
  {
    "slug": "bikaner",
    "name": "Bikaner",
    "state": "Rajasthan",
    "heroTagline": "Bikaner Taxi, Car Rental & Tempo Traveller Booking",
    "attractions": [
      "Junagarh Fort",
      "Rampuria Havelis",
      "Lalgarh Palace and Museum",
      "Karni Mata Temple, Deshnok"
    ],
    "description": "Arrange Bikaner taxi service and car hire for Junagarh Fort, havelis and Deshnok visits. Compare cab booking and Tempo Traveller rental for Rajasthan travel.",
    "pickup": "Your Bikaner hotel, railway arrival point or an agreed road-accessible city address",
    "taxiIntro": [
      "Junagarh Fort, the Rampuria havelis and Lalgarh Palace give Bikaner a varied heritage itinerary. A Deshnok Karni Mata Temple visit is a separate excursion. Tell us which sites require a longer visit and which are photo or shopping stops, so the road duty includes appropriate waiting.",
      "For the old-city haveli area, share the hotel or meeting-point pin and confirm where the chosen vehicle can approach and wait. Send railway details for an arrival transfer, with bags and any sightseeing before hotel check-in. A group may need a common boarding point rather than collections inside narrow lanes.",
      "Car hire can cover Bikaner city travel, a Deshnok return or a longer Rajasthan journey towards Jaisalmer, Jodhpur or Jaipur. Compare a car or MPV for a smaller party with a Tempo Traveller for several families and their luggage. The selected vehicle, accessible pickup points, overnight duty and full fare are confirmed after the complete itinerary is reviewed."
    ],
    "tempoUseCases": [
      {
        "title": "Junagarh and Bikaner heritage group",
        "desc": "List the fort, Lalgarh and haveli stops with their expected visit durations. Agree a road-accessible pickup for the group and a return meeting point after any old-city walk."
      },
      {
        "title": "Deshnok visit and Rajasthan onward transfer",
        "desc": "Share the Bikaner hotel, temple excursion and final city or overnight hotel. Include bags and the return plan so the Traveller quote covers the complete road journey."
      }
    ],
    "tripPlanning": [
      {
        "title": "Bikaner hotel → Junagarh Fort → Rampuria Havelis → hotel",
        "description": "Allow for a fort visit and a separate walking or photo stop in the haveli area. Confirm suitable vehicle parking and the collection point."
      },
      {
        "title": "Bikaner → Deshnok Karni Mata Temple → Bikaner",
        "description": "Provide the temple-visit plan, passenger needs and return hotel. Agree waiting and the accessible meeting point before confirming the trip."
      },
      {
        "title": "Bikaner → Jaisalmer or Jodhpur",
        "description": "State the onward hotel, intermediate stops and whether the vehicle returns with you. Luggage and overnight arrangements help confirm the appropriate vehicle."
      }
    ],
    "nearbyCities": [
      "jaisalmer",
      "jodhpur",
      "jaipur"
    ],
    "note": "For Bikaner, distinguish Junagarh and old-city visits from a Deshnok excursion. Give the hotel entrance, accessible haveli meeting point, luggage and any onward Rajasthan stay when requesting a vehicle and full quote.",
    "intent": "general"
  },
  {
    "slug": "ranthambore",
    "name": "Ranthambore",
    "state": "Rajasthan",
    "heroTagline": "Ranthambore Road Transfers, Taxi & Tempo Traveller Rental",
    "attractions": [
      "Ranthambore National Park and Tiger Reserve",
      "Ranthambore Fort"
    ],
    "description": "Book Ranthambore taxi and car hire for Sawai Madhopur arrivals, resort transfers and agreed road sightseeing. Request Tempo Traveller hire; safaris are separate.",
    "pickup": "Sawai Madhopur railway station, your Ranthambore resort entrance or the reporting point stated in your separate safari booking",
    "taxiIntro": [
      "Ranthambore is a wildlife destination near Sawai Madhopur with a national park, tiger reserve and historic fort. Begin by separating your resort and other road transfers from the wildlife safari. Send the reserve reporting details from the safari operator rather than asking a normal taxi to enter a safari zone.",
      "A Sawai Madhopur station pickup needs the train time, group contact and exact resort entrance. Safari reporting points and timings should be copied from the confirmed permit or operator instructions; a fort visit needs its own permitted drop and return plan. Check current access for the travel date before the road-hire schedule is finalised.",
      "Choose a car or MPV for a couple or family with bags, or request a Tempo Traveller for a larger resort group and luggage. One-way or return travel from Jaipur, Kota or another agreed starting city can be quoted with the resort transfers. Park permits, safari vehicles, guides and wildlife sightings are separate from the confirmed road-transport booking."
    ],
    "tempoUseCases": [
      {
        "title": "Sawai Madhopur arrival and resort group transfer",
        "desc": "Share the train arrival, resort entrance and luggage count. Confirm the Traveller pickup point and departure collection, with enough detail for the group accommodation transfer."
      },
      {
        "title": "Jaipur–Ranthambore family or group holiday",
        "desc": "List the Jaipur pickup, resort nights and final drop. Include only the required road legs in the Traveller request and keep safari permits, reporting arrangements and safari vehicles separately confirmed."
      }
    ],
    "tripPlanning": [
      {
        "title": "Sawai Madhopur station → Ranthambore resort",
        "description": "Use the exact resort name and entrance pin with the train details. Add a return station transfer if required, identifying all passengers and bags."
      },
      {
        "title": "Resort → agreed safari reporting point → resort",
        "description": "Copy the time and meeting point from your separately confirmed safari instructions. Confirm whether a separate road transfer is needed and who handles the return collection."
      },
      {
        "title": "Jaipur → Ranthambore stay → Kota or Jaipur",
        "description": "List every overnight hotel and final drop. Include any permitted fort road transfer separately from the safari vehicle and park arrangements."
      }
    ],
    "nearbyCities": [
      "jaipur",
      "kota"
    ],
    "note": "For Ranthambore, give the exact resort, Sawai Madhopur arrival and any reporting point from your safari booking. Confirm permitted road access and separate fort waiting; park permits, safari vehicles and guides are not included in taxi or Traveller road hire.",
    "intent": "general"
  },
  {
    "slug": "chittorgarh",
    "name": "Chittorgarh",
    "state": "Rajasthan",
    "heroTagline": "Chittorgarh Taxi Service, Car Hire & Group Traveller Booking",
    "attractions": [
      "Chittorgarh Fort",
      "Vijay Stambh",
      "Kirti Stambh",
      "Fateh Prakash Palace Museum"
    ],
    "description": "Arrange Chittorgarh taxi service and car hire for fort monuments, railway transfers and onward Udaipur trips. Compare cab and Tempo Traveller rental options.",
    "pickup": "Your Chittorgarh hotel, railway arrival point or an agreed road-accessible fort meeting point",
    "taxiIntro": [
      "Chittorgarh Fort is a large heritage area with several separate places to visit, including Vijay Stambh, Kirti Stambh and Fateh Prakash Palace Museum. Name the monuments you want to see and the time for walking, rather than booking only a brief drop at the fort entrance.",
      "Send your hotel or railway arrival details and agree the vehicle approach, permitted parking and return meeting points for the fort visit. Tell us about passengers who need easier boarding or shorter walks before selecting the vehicle. Entry tickets, guides and any separately chosen attraction programme should be arranged apart from road hire.",
      "A car or MPV can suit a family heritage trip, while a Tempo Traveller can keep a larger party on one collection schedule. Chittorgarh can be a stay or a planned sightseeing stop on an Udaipur or Kota journey. Share the overnight addresses, luggage and final city so the selected vehicle, waiting and complete fare can be confirmed."
    ],
    "tempoUseCases": [
      {
        "title": "Chittorgarh Fort monument circuit",
        "desc": "List Vijay Stambh, Kirti Stambh, the museum and any additional stops with visit durations. Agree accessible group drop and return points and account for time spent exploring on foot."
      },
      {
        "title": "Udaipur connection with a Chittorgarh visit",
        "desc": "Send the starting hotel, fort-visit plan and final overnight address. Include luggage and whether you need a one-way transfer or a return Traveller duty."
      }
    ],
    "tripPlanning": [
      {
        "title": "Chittorgarh hotel → fort monuments → hotel",
        "description": "List the individual monuments and desired walking time. Confirm permitted road access and the parking points for the chosen car or Traveller."
      },
      {
        "title": "Udaipur → Chittorgarh Fort → Udaipur",
        "description": "Share your hotel pickup and the monuments you want to enter. Include the complete return duty and waiting in the quote request."
      },
      {
        "title": "Chittorgarh railway station → fort visit → Kota or Udaipur",
        "description": "Give the train arrival and final hotel address, with luggage and every sightseeing stop. Confirm the one-way itinerary before choosing the vehicle."
      }
    ],
    "nearbyCities": [
      "udaipur",
      "kota"
    ],
    "note": "For Chittorgarh, specify the monuments inside the fort area and allow for walking between visits. Agree permitted parking and return meeting points, then include train details, overnight stays and any Udaipur or Kota leg in the full quote.",
    "intent": "general"
  },
  {
    "slug": "kota",
    "name": "Kota",
    "state": "Rajasthan",
    "heroTagline": "Kota Taxi, Cab Booking & Tempo Traveller Rental",
    "attractions": [
      "Garh Palace and Rao Madho Singh Museum",
      "Kishore Sagar",
      "Seven Wonders Park",
      "Chambal Riverfront"
    ],
    "description": "Book Kota taxi service and car hire for railway pickup, family visits and Chambal sightseeing. Request cab or Tempo Traveller rental for your full itinerary.",
    "pickup": "Kota Junction, your hotel or residence, or the exact institute or event-venue gate",
    "taxiIntro": [
      "Kota offers palace and waterside sightseeing: Garh Palace houses the Rao Madho Singh Museum, while Kishore Sagar, Seven Wonders Park and the Chambal Riverfront provide different outing stops. List the places you want to visit and whether you need time for walking or a return pickup after an independently arranged activity.",
      "A Kota Junction transfer should include train details, bags and the full hotel, home or hostel address. For an institute, office or event visit, provide the gate and reporting time and identify any additional family pickups. Confirm the accessible entrance and return collection rather than relying on the area name alone.",
      "Cars, MPVs and SUVs can be compared for family, student-arrival or business trips; a Tempo Traveller can be requested for a larger group travelling together. Outstation hire may link Kota with Ranthambore, Chittorgarh or Jaipur. Include the complete stop order, nights and final drop so vehicle availability, waiting and the full road-hire quote can be confirmed."
    ],
    "tempoUseCases": [
      {
        "title": "Kota family arrivals and city outing",
        "desc": "Share the train arrivals, hotel or hostel entrances and the Garh Palace or lakeside stops. Include passengers and bags so the group vehicle and collection sequence can be confirmed."
      },
      {
        "title": "Kota with Ranthambore or Chittorgarh",
        "desc": "Provide all hotel nights and sightseeing stops, plus the final city or railway drop. Keep any wildlife safari or boat outing separately arranged from the Traveller road journey."
      }
    ],
    "tripPlanning": [
      {
        "title": "Kota Junction → hotel or hostel → agreed city stops",
        "description": "Give the train details, exact entrance and any institute reporting time. Include bags and additional family collections in the transfer schedule."
      },
      {
        "title": "Kota hotel → Garh Palace → Kishore Sagar → Chambal Riverfront",
        "description": "Choose the visits and time for walking at each stop. Confirm the collection points and arrange attraction tickets or boat activities separately."
      },
      {
        "title": "Kota → Ranthambore or Chittorgarh",
        "description": "Specify the resort or hotel, sightseeing plan and one-way or return arrangement. An overnight itinerary needs the full accommodation sequence and final drop."
      }
    ],
    "nearbyCities": [
      "ranthambore",
      "chittorgarh",
      "jaipur"
    ],
    "note": "For Kota, send the exact station, hotel, hostel or venue gate and reporting time. Separate Garh Palace and waterfront waiting from onward Ranthambore or Chittorgarh travel, and include all passengers, bags and return collections in your quote request.",
    "intent": "general"
  },
  {
    "slug": "dehradun",
    "name": "Dehradun",
    "state": "Uttarakhand",
    "heroTagline": "Dehradun Taxi Service, Car Hire & Tempo Traveller Booking",
    "attractions": [
      "Robber's Cave (Gucchupani)",
      "Sahastradhara",
      "Tapkeshwar Mahadev Temple"
    ],
    "description": "Request Dehradun taxi, cab booking and car hire for city visits, railway or airport transfers and Mussoorie journeys. Compare Tempo Traveller options for your group.",
    "pickup": "Your Dehradun hotel or address, Dehradun Railway Station or the airport meeting point confirmed for your flight",
    "taxiIntro": [
      "Dehradun sightseeing can combine Robber's Cave, Sahastradhara and Tapkeshwar Mahadev Temple, with different amounts of walking at each stop. Select the places you want to visit and tell us whether the vehicle should wait or return at an agreed time. A city itinerary should be planned separately from the onward hill journey to Mussoorie.",
      "For railway or airport collection, send the arrival details from your ticket and your complete hotel, campus or meeting address. Give the actual entrance pin for an institution or event venue. If several guests arrive separately, list every collection time and luggage count so the team can confirm an appropriate car, SUV or Tempo Traveller.",
      "Dehradun can be the start or finish of a family holiday, a Mussoorie transfer or a journey towards Rishikesh and Haridwar. Share all overnight stays and the final return connection, and specify a one-way drop or a vehicle retained for the tour. Availability, route access, waiting and the complete road-hire fare are confirmed before booking."
    ],
    "tempoUseCases": [
      {
        "title": "Railway or airport arrival for a hill holiday",
        "desc": "Provide one group contact, arrival details and all bags, followed by the exact Dehradun or Mussoorie hotel. Confirm vehicle suitability and the accessible hotel entrance before the group travels."
      },
      {
        "title": "City sightseeing and family event transport",
        "desc": "List Robber's Cave, Sahastradhara or temple stops alongside any event reporting times. Agree waiting, hotel collections and the final drop so the Traveller duty matches the full schedule."
      }
    ],
    "tripPlanning": [
      {
        "title": "Dehradun station or airport → Mussoorie hotel",
        "description": "Send the arrival ticket and hill-hotel entrance pin. Specify whether this is a one-way transfer or part of a stay with a return collection; local Mussoorie sightseeing is quoted separately when requested."
      },
      {
        "title": "Dehradun → Robber's Cave → Sahastradhara → Dehradun",
        "description": "Choose visit durations and agree parking meeting points. Include any additional Tapkeshwar stop and time for walking when the full city duty is quoted."
      },
      {
        "title": "Dehradun → Rishikesh → Haridwar",
        "description": "List the ashram, ghat or hotel addresses in route order, with the final railway or hotel drop. Confirm whether the vehicle stays overnight or finishes at the last stop."
      }
    ],
    "nearbyCities": [
      "mussoorie",
      "rishikesh",
      "haridwar"
    ],
    "note": "For Dehradun, send the exact station or airport arrival details and hotel, campus or venue entrance. Separate city sightseeing from a Mussoorie hill transfer; provide all overnight stops, bags and the final drop to confirm the selected vehicle and full fare.",
    "intent": "general"
  },
  {
    "slug": "mussoorie",
    "name": "Mussoorie",
    "state": "Uttarakhand",
    "heroTagline": "Mussoorie Taxi, Cab Booking & Tempo Traveller Rental",
    "attractions": [
      "Kempty Falls",
      "Landour and Lal Tibba",
      "Mall Road",
      "Mussoorie Lake"
    ],
    "description": "Arrange Mussoorie taxi and car hire for hotel transfers, Kempty Falls and hill sightseeing. Request cab or Tempo Traveller rental with vehicle access confirmed.",
    "pickup": "Your Mussoorie or Landour hotel entrance, an agreed accessible town meeting point or your Dehradun arrival location",
    "taxiIntro": [
      "Mussoorie's hill visits can include Kempty Falls, Mussoorie Lake and Landour's Lal Tibba area, alongside time on Mall Road. These are separate stops with different approach roads and walking requirements. Tell us which are essential for your family so the itinerary allows time at the places you actually want to explore.",
      "A hotel booking should identify the entrance a vehicle can reach, rather than only a Mussoorie property name. Send the pin and any approach guidance supplied by the hotel, especially for Landour or a narrow hill road. For a Dehradun pickup, include flight or train details, luggage and the final hill address before choosing a car or Traveller.",
      "Request a one-way hotel transfer, a return holiday booking or a sightseeing vehicle retained for agreed days. List each hotel change, Kempty excursion and final Dehradun departure. The team confirms the selected vehicle, current access and waiting arrangements; ropeway rides, attraction tickets and other activities are separate from road hire."
    ],
    "tempoUseCases": [
      {
        "title": "Dehradun arrival and Mussoorie family stay",
        "desc": "Share the arrival details, group luggage and hotel approach information. Confirm that the selected Traveller suits the route and arrange the return train or flight collection as part of the booking."
      },
      {
        "title": "Hill sightseeing for a group",
        "desc": "List Kempty Falls, Landour or other chosen stops with expected visit durations. Agree parking and regrouping points, and confirm any short local transport needed where the Traveller cannot approach."
      }
    ],
    "tripPlanning": [
      {
        "title": "Dehradun → Mussoorie hotel → Dehradun",
        "description": "Provide arrival and departure connections plus the hotel entrance. Decide whether the car waits through the stay or returns for a separately agreed collection."
      },
      {
        "title": "Mussoorie hotel → Kempty Falls → Mussoorie",
        "description": "Keep the waterfall excursion distinct from town walks. Include walking time and the confirmed vehicle meeting point in the duty and waiting quote."
      },
      {
        "title": "Mussoorie → Landour and Lal Tibba → hotel",
        "description": "Share the precise places you want to visit and check the approach for the selected vehicle. Agree where the group will continue on foot and where it will regroup."
      }
    ],
    "nearbyCities": [
      "dehradun",
      "rishikesh"
    ],
    "note": "For Mussoorie, provide the actual hotel entrance and approach instructions, not only the town name. Separate Kempty Falls, Landour and town walks in the itinerary; confirm current access and suitable vehicle size before agreeing waiting and the full fare.",
    "intent": "general"
  },
  {
    "slug": "nainital",
    "name": "Nainital",
    "state": "Uttarakhand",
    "heroTagline": "Nainital Taxi Service, Car Hire & Tempo Traveller Booking",
    "attractions": [
      "Naini Lake",
      "Naina Devi Temple",
      "Snow View Point",
      "Bhimtal — separate lake excursion"
    ],
    "description": "Request Nainital taxi and car hire for Kathgodam transfers, lake visits and family travel. Compare cab booking and Tempo Traveller rental with a full itinerary quote.",
    "pickup": "Your Nainital hotel, an agreed accessible Mallital or Tallital meeting point, or Kathgodam Railway Station",
    "taxiIntro": [
      "Naini Lake and the lakeside Naina Devi Temple form the centre of many Nainital visits. Snow View and a separate excursion to Bhimtal can be added to the plan. Choose the stops you want and set aside time for walking or separately booked boating instead of treating every visit as a brief vehicle drop.",
      "For a Kathgodam Railway Station arrival, send the train details, number of bags and complete Nainital hotel address. Specify Mallital, Tallital or the actual hillside entrance when sharing the pickup pin. The group should agree where the vehicle can wait and where everyone will meet after a lakeside walk or temple visit.",
      "A Nainital booking can cover a one-way hill transfer, return family holiday, lake sightseeing or an onward Jim Corbett stay. List each resort and reporting time, including any safari connection. Compare vehicle seating and luggage space, then confirm route access, waiting, overnight duties and the complete fare for the requested travel dates."
    ],
    "tempoUseCases": [
      {
        "title": "Kathgodam arrival and Nainital hotel transfer",
        "desc": "Send the train arrival and final hotel entrance with the passenger and luggage totals. Confirm a suitable Traveller approach, meeting point and return station collection before travel."
      },
      {
        "title": "Nainital lakes and Corbett group holiday",
        "desc": "List the Nainital visits, Bhimtal stop if wanted and exact Corbett resort address. Include any separately booked safari reporting time so the road transfers fit the group's commitments."
      }
    ],
    "tripPlanning": [
      {
        "title": "Kathgodam Railway Station → Nainital hotel",
        "description": "Copy the train details from the ticket, provide bags and the hotel entrance pin, and agree the hill transfer and final drop before dispatch."
      },
      {
        "title": "Nainital → Bhimtal → Nainital",
        "description": "Choose the lake stops and waiting time, including separately arranged boat activities. Confirm the return hotel and any additional viewpoints as part of the route quote."
      },
      {
        "title": "Nainital → Ramnagar / Corbett resort",
        "description": "Provide the exact resort rather than only Jim Corbett, and send the safari gate and reporting time if a connection is required. Park permits and safari vehicles are separate."
      }
    ],
    "nearbyCities": [
      "jim-corbett"
    ],
    "note": "For Nainital, specify Kathgodam station or the exact Mallital, Tallital or hotel pickup. Agree the accessible entrance, lake-visit waiting and return collection; any Corbett leg needs the precise resort and separately confirmed safari details.",
    "intent": "general"
  },
  {
    "slug": "kedarnath",
    "name": "Kedarnath",
    "state": "Uttarakhand",
    "heroTagline": "Kedarnath Road Transfer, Taxi & Tempo Traveller Hire",
    "attractions": [
      "Kedarnath Temple — onward journey from the roadhead",
      "Gaurikund",
      "Sonprayag",
      "Mandakini valley"
    ],
    "description": "Request Kedarnath taxi and Tempo Traveller road transfers to the permitted Sonprayag or Gaurikund access point. Plan the onward temple journey separately.",
    "pickup": "Your Haridwar, Rishikesh or Dehradun arrival point, Guptkashi hotel or agreed permitted Sonprayag-area road meeting point",
    "taxiIntro": [
      "Kedarnath Temple is reached by an onward journey from the roadhead; an ordinary taxi or Tempo Traveller does not drive to the shrine. The trek starts from Gaurikund. Plan the road-hire portion around your accommodation and the permitted vehicle endpoint in the Sonprayag–Gaurikund area, with any required local transfer confirmed separately.",
      "Send the complete arrival details, Guptkashi or other overnight hotel addresses, planned temple-visit day and the point where the group will rejoin the driver. Do not use the temple name as a vehicle pickup pin. Registration arrangements, seasonal access, local traffic controls and the agreed last road point need confirmation for the travel dates.",
      "A car, SUV or suitable Tempo Traveller can be requested for the road stages from Haridwar, Rishikesh or Dehradun and for an onward Badrinath itinerary. Share passengers, bags and all overnight duties. The quote covers only the agreed road transport; trekking support, local shuttle services, accommodation and any helicopter arrangement are separate."
    ],
    "tempoUseCases": [
      {
        "title": "Family road transfer to the Kedarnath approach",
        "desc": "Provide the group's arrival point, overnight stay and permitted Sonprayag-area drop. Confirm the onward local transfer and temple journey separately, with a clear road meeting point and date for the return collection."
      },
      {
        "title": "Kedarnath and Badrinath road itinerary",
        "desc": "List each hotel night and the day the group returns from the Kedarnath trek. Confirm the vehicle's waiting or return duty before continuing towards Badrinath, subject to current road access."
      }
    ],
    "tripPlanning": [
      {
        "title": "Rishikesh / Haridwar → Guptkashi → permitted Sonprayag-area drop",
        "description": "Agree the overnight halt and lawful endpoint for the selected vehicle. The onward Gaurikund connection and temple trek are separate from the main road-transfer booking."
      },
      {
        "title": "Kedarnath roadhead collection → Rudraprayag → Badrinath itinerary",
        "description": "Specify when and where the group will be back on the road, then list the onward hotel nights. Do not schedule the road pickup at Kedarnath Temple itself."
      }
    ],
    "nearbyCities": [
      "badrinath",
      "rishikesh",
      "haridwar"
    ],
    "note": "Kedarnath vehicle hire is for the road stages only. Confirm the permitted Sonprayag–Gaurikund endpoint, any separate local transfer, trek arrangements and return meeting point. Send hotel nights, registration plans and dates so seasonal access, the selected vehicle and complete road fare can be checked.",
    "intent": "pilgrimage"
  },
  {
    "slug": "badrinath",
    "name": "Badrinath",
    "state": "Uttarakhand",
    "heroTagline": "Badrinath Taxi, Car Hire & Tempo Traveller Pilgrimage Travel",
    "attractions": [
      "Badrinath Temple",
      "Tapt Kund",
      "Alaknanda riverfront",
      "Mana village — access confirmed separately"
    ],
    "description": "Arrange Badrinath taxi and Tempo Traveller road travel with hotel stops, temple waiting and return plans. Vehicle access and seasonal travel are confirmed in advance.",
    "pickup": "Your agreed Haridwar or Rishikesh starting point, Joshimath hotel or a permitted Badrinath road meeting point",
    "taxiIntro": [
      "Badrinath is a Char Dham pilgrimage town beside the Alaknanda, with Badrinath Temple and Tapt Kund among the principal visits. An itinerary may also request Mana village as a separate stop. Name the visits and hotel stays you want, and allow time between the vehicle drop and the temple or village walks.",
      "Road travel should identify the exact Badrinath accommodation and agreed parking or meeting point. If the journey starts at a railway station or airport, include the arrival details and the intermediate hotel nights. Confirm registration arrangements, seasonal opening and current road access before fixing a departure; no page description guarantees year-round access.",
      "Compare a car, SUV or suitable Tempo Traveller for a Badrinath return journey, an Auli stay or a wider pilgrimage itinerary. Include every passenger, bag and overnight duty and specify the final Haridwar, Rishikesh or other drop. The team confirms the vehicle and full road-hire quote; temple arrangements, entry costs and accommodation remain separate."
    ],
    "tempoUseCases": [
      {
        "title": "Badrinath pilgrimage with planned hotel nights",
        "desc": "List the starting station or hotel, each overnight stay and the temple visit day. Agree the Traveller's accessible drop and regrouping point, waiting and the final return connection."
      },
      {
        "title": "Badrinath with an Auli stay",
        "desc": "Provide both the Badrinath and Auli hotel entrances and the intended travel days. Confirm the road approach for the selected vehicle and arrange any local activity or alternate mountain transfer separately."
      }
    ],
    "tripPlanning": [
      {
        "title": "Rishikesh → Rudraprayag → Joshimath → Badrinath",
        "description": "Plan the overnight addresses and temple visit day before the fare is quoted. Confirm current seasonal access and the final Badrinath parking or hotel drop."
      },
      {
        "title": "Badrinath → requested Mana visit → Badrinath hotel",
        "description": "Treat Mana as a separate sightseeing request with current access confirmation. Include walking time and a clear regrouping point in the vehicle duty."
      },
      {
        "title": "Badrinath → Joshimath → Auli stay",
        "description": "Send the precise Auli property and confirm its approach for the chosen vehicle. Activity bookings and any separately needed local transport are not included automatically."
      }
    ],
    "nearbyCities": [
      "auli",
      "kedarnath"
    ],
    "note": "For Badrinath, send all hotel nights and the temple visit date, then confirm seasonal access, registration arrangements and the vehicle meeting point. List Mana or Auli separately and include the final return city in the complete road-hire quote.",
    "intent": "pilgrimage"
  },
  {
    "slug": "gangotri",
    "name": "Gangotri",
    "state": "Uttarakhand",
    "heroTagline": "Gangotri Taxi Service, Car Hire & Tempo Traveller Booking",
    "attractions": [
      "Gangotri Temple",
      "Bhagirathi riverfront",
      "Bhagirath Shila",
      "Harsil — a separate route stop"
    ],
    "description": "Request Gangotri taxi and Tempo Traveller road travel through Uttarkashi and Harsil. Plan temple visits, hotel nights and return pickup with access confirmed.",
    "pickup": "Your confirmed Haridwar, Rishikesh or Dehradun starting point, Uttarkashi or Harsil hotel, or agreed Gangotri road meeting point",
    "taxiIntro": [
      "Gangotri Temple stands beside the Bhagirathi, with Bhagirath Shila and the riverfront forming part of a pilgrimage visit. Harsil can be included as a separate stop on the approach itinerary. State whether your priority is temple darshan, a Harsil stay or both so road travel and waiting are quoted for the right plan.",
      "A Gangotri booking needs each Uttarkashi, Harsil or other overnight address, the temple visit day and the final return destination. Confirm the permitted vehicle drop and regrouping point for your selected car or Traveller. Seasonal opening, registration arrangements and current road access should be checked for the actual dates rather than assumed from a general itinerary.",
      "Gangotri town road travel and a trek towards Gaumukh are different arrangements. If the group plans onward trekking, provide the authorised reporting point and the date it will return to the road; permits, guides and trekking support are separate. The team confirms the vehicle, waiting or standby duty and complete fare for the agreed road stages."
    ],
    "tempoUseCases": [
      {
        "title": "Uttarkashi, Harsil and Gangotri group pilgrimage",
        "desc": "Share all hotel nights and the temple visit schedule, plus luggage and mobility needs. Agree suitable Traveller parking and the group meeting point after darshan or a riverfront walk."
      },
      {
        "title": "Road transfers for a separately arranged trek",
        "desc": "Provide the confirmed trek reporting location and return date, with one group contact. Keep permits, trekking services and the road vehicle duty separately identified in your bookings."
      }
    ],
    "tripPlanning": [
      {
        "title": "Rishikesh / Haridwar → Uttarkashi → Harsil → Gangotri",
        "description": "Choose the overnight stops and any Harsil visit before requesting a fare. Confirm current access and an agreed road meeting point at Gangotri."
      },
      {
        "title": "Harsil hotel → Gangotri visit → Harsil hotel",
        "description": "Specify temple waiting and the return collection time. The quote covers the agreed road journey; any onward Gaumukh trek is arranged separately."
      },
      {
        "title": "Gangotri road collection → Uttarkashi → Yamunotri approach itinerary",
        "description": "List the intervening hotel nights and Janki Chatti roadhead plan. Treat Yamunotri's onward trek as a separate stage and confirm the full multi-day vehicle duty."
      }
    ],
    "nearbyCities": [
      "yamunotri",
      "rishikesh"
    ],
    "note": "For Gangotri, provide Uttarkashi or Harsil hotel details, the temple visit day and the final return plan. Confirm seasonal access and a road meeting point. A Gaumukh trek, its permits and guides are separate from the taxi or Traveller road booking.",
    "intent": "pilgrimage"
  },
  {
    "slug": "yamunotri",
    "name": "Yamunotri",
    "state": "Uttarakhand",
    "heroTagline": "Yamunotri Road Transfer, Taxi & Tempo Traveller Hire",
    "attractions": [
      "Yamunotri Temple — onward trek from Janki Chatti",
      "Surya Kund",
      "Divya Shila",
      "Janki Chatti roadhead"
    ],
    "description": "Arrange Yamunotri taxi and Tempo Traveller road transfers to Janki Chatti, with the onward temple trek separate. Confirm hotel nights, access and return collection.",
    "pickup": "Your Dehradun, Haridwar or Rishikesh starting point, Barkot hotel or an agreed permitted Janki Chatti road meeting point",
    "taxiIntro": [
      "Yamunotri Temple is reached by an onward trek from Janki Chatti, the last motorable point on the usual approach. Taxis and Tempo Travellers do not drive to the shrine. The temple visit can include Surya Kund and Divya Shila, but the vehicle booking covers only the agreed road stages and return collection.",
      "Send your starting station or hotel, Barkot or other overnight accommodation and the planned trek day. Agree the vehicle's permitted Janki Chatti drop and the point where the group will rejoin it. Confirm seasonal opening, registration arrangements, local access and the selected vehicle's suitability before the travel schedule is finalised.",
      "A family or group can request a one-way roadhead transfer, a return itinerary or a combined Yamunotri and Gangotri road journey. Include passengers, luggage, hotel nights and any standby days. The full road fare is confirmed in advance; trekking assistance, pony or palanquin services, accommodation and temple arrangements are separate bookings."
    ],
    "tempoUseCases": [
      {
        "title": "Barkot stay and Janki Chatti roadhead transfer",
        "desc": "Provide the hotel entrance, planned trek day and the group's bags. Confirm the Traveller's permitted drop, onward trek arrangements and the time and place for everyone to rejoin the driver."
      },
      {
        "title": "Yamunotri and Gangotri group pilgrimage",
        "desc": "List the Barkot, Uttarkashi and other hotel nights, together with roadhead return dates. Confirm the vehicle duty for the road stages and arrange temple treks or other local services separately."
      }
    ],
    "tripPlanning": [
      {
        "title": "Dehradun → Barkot → Janki Chatti roadhead",
        "description": "Give the arrival and overnight details, and agree the accessible vehicle endpoint. The onward Yamunotri temple trek is separate from the road-transfer booking."
      },
      {
        "title": "Barkot hotel → Janki Chatti → Barkot hotel",
        "description": "Plan the trek day and a clear return meeting point, including the vehicle's waiting arrangements. Confirm current road access and any local transfer requirement."
      },
      {
        "title": "Janki Chatti road collection → Uttarkashi → Gangotri itinerary",
        "description": "Provide the return-from-trek date and each onward hotel night. Quote the entire road duty after checking current access and the group's travel schedule."
      }
    ],
    "nearbyCities": [
      "gangotri",
      "dehradun"
    ],
    "note": "Yamunotri vehicle hire ends at the agreed permitted Janki Chatti road point; the temple requires an onward trek. Confirm seasonal access, registration arrangements, separate trekking services, hotel nights and the return meeting point before the complete road fare is agreed.",
    "intent": "pilgrimage"
  },
  {
    "slug": "jim-corbett",
    "name": "Jim Corbett",
    "state": "Uttarakhand",
    "heroTagline": "Jim Corbett Resort Transfers, Taxi & Tempo Traveller Hire",
    "attractions": [
      "Jim Corbett National Park — safari arrangements separate",
      "Ramnagar",
      "Kosi river landscape",
      "Corbett-area resort stays"
    ],
    "description": "Request Jim Corbett taxi and Tempo Traveller hire for Ramnagar, resort and safari reporting-point road transfers. Park permits and safari vehicles are separate.",
    "pickup": "Ramnagar Railway Station, your exact Corbett-area resort or the road-accessible reporting point shown on your safari arrangements",
    "taxiIntro": [
      "A Jim Corbett trip should identify the actual resort and safari reporting point, not only the park's name. Corbett's wildlife visits are organised for specific zones and reporting locations. The taxi or Traveller plan can cover the road arrival, accommodation transfers and agreed pickup outside the safari operation.",
      "Send the Ramnagar train details or other starting address, resort entrance pin and the zone, gate and reporting time from your confirmed safari booking. Do not assume a normal hired car or Tempo Traveller can replace the authorised safari vehicle. Entry permits, guides, safari transport and wildlife sightings are not included or guaranteed by road hire.",
      "For a family holiday, corporate group or an itinerary combining Nainital and Corbett, list each resort stay and final station or city drop. Compare passenger and luggage capacity for the road journey, then confirm the vehicle, waiting and full fare. Any change to the safari schedule should be shared before the road pickup plan is finalised."
    ],
    "tempoUseCases": [
      {
        "title": "Ramnagar arrival and group resort transfer",
        "desc": "Share the train arrival, passenger and bag totals and the exact resort entrance. Confirm vehicle access and the return station collection, especially where the property is outside Ramnagar town."
      },
      {
        "title": "Nainital and Corbett family or corporate stay",
        "desc": "Provide the Nainital hotel and all Corbett resort or event addresses. Add the confirmed safari reporting time so the Traveller transfers are coordinated with separately booked park activities."
      }
    ],
    "tripPlanning": [
      {
        "title": "Ramnagar Railway Station → confirmed Corbett resort",
        "description": "Use the full resort name and entrance pin, with arrival details and bags. Confirm the road pickup and final drop instead of booking only against the broad park name."
      },
      {
        "title": "Corbett resort → confirmed safari reporting point → resort",
        "description": "Send the gate, zone and timing printed on your safari confirmation. This is a road transfer; the park entry and authorised safari vehicle remain separate arrangements."
      },
      {
        "title": "Nainital hotel → Corbett resort → Ramnagar station",
        "description": "List both stays and the final train connection. Include luggage, hotel checkout times and any separate safari commitment in the complete road itinerary."
      }
    ],
    "nearbyCities": [
      "nainital"
    ],
    "note": "For Jim Corbett, send the exact resort and the gate, zone and reporting time from your safari confirmation. The car or Traveller is for agreed road transfers; park permits, guides and authorised safari vehicles are separate, and wildlife sightings are not guaranteed.",
    "intent": "general"
  },
  {
    "slug": "auli",
    "name": "Auli",
    "state": "Uttarakhand",
    "heroTagline": "Auli Taxi, Car Hire & Tempo Traveller Mountain Transfers",
    "attractions": [
      "Auli meadows and ski slopes",
      "Himalayan mountain views",
      "Joshimath / Jyotirmath",
      "Gorson meadow excursion — walking arrangements separate"
    ],
    "description": "Arrange Auli taxi and Tempo Traveller road transfers with Joshimath hotel details and current access confirmed. Request car hire for the agreed mountain itinerary.",
    "pickup": "Your confirmed Rishikesh starting point, Joshimath / Jyotirmath hotel or agreed accessible Auli accommodation entrance",
    "taxiIntro": [
      "Auli is a mountain and skiing destination above Joshimath, also known as Jyotirmath, in Chamoli. Its meadows and Himalayan views attract visitors with different plans: a hotel stay, a skiing session or a walking excursion towards Gorson. Tell us which activities you have arranged so the road transfer reaches the appropriate meeting point.",
      "Send the exact accommodation address and confirm whether your stay is in Joshimath or Auli. Weather and road conditions can change the approach suitable for a car or larger Traveller, so the final vehicle endpoint must be agreed for your dates. Any local transfer, ropeway or chairlift arrangement should be checked separately with its operator rather than assumed to be running.",
      "Auli road hire can form part of a return journey from Rishikesh or a wider trip including Badrinath. Provide each overnight stay, activity reporting time, passenger and luggage count, and the final drop. The team confirms vehicle suitability and the full road fare; ski equipment, lessons, walking guides and other mountain activities are separate."
    ],
    "tempoUseCases": [
      {
        "title": "Group transfer for an Auli stay",
        "desc": "Provide the Joshimath or Auli hotel entrance and all passenger and luggage details. Confirm the selected Traveller's permitted approach and any separate final local transfer before the group arrives."
      },
      {
        "title": "Auli and Badrinath multi-day itinerary",
        "desc": "List both destinations' hotel nights and the planned activity or temple visit days. Confirm seasonal road access and each vehicle meeting point, with mountain activities and temple arrangements booked separately."
      }
    ],
    "tripPlanning": [
      {
        "title": "Rishikesh → Joshimath / Jyotirmath → Auli stay",
        "description": "Share all overnight addresses and the exact mountain property. Confirm the vehicle's final approach for the travel date and any separate local transfer."
      },
      {
        "title": "Joshimath hotel → agreed Auli drop → Joshimath hotel",
        "description": "Specify activity reporting and return collection times. Road access and any ropeway or chairlift operation must be confirmed separately; the vehicle quote does not include activity tickets."
      },
      {
        "title": "Auli / Joshimath → Badrinath itinerary",
        "description": "Include the Badrinath visit date, accommodation and return city. Confirm seasonal access and waiting before combining the road stages into one hire quote."
      }
    ],
    "nearbyCities": [
      "badrinath"
    ],
    "note": "For Auli, identify whether the hotel is in Joshimath / Jyotirmath or Auli and send its entrance pin. Confirm weather-dependent vehicle access and any separate last-mile transfer; include activity timings, hotel nights and the final return plan in the road-hire quote.",
    "intent": "general"
  }
];
